"use server";

import { UserRole } from "@prisma/client";
import { formatarInstante, FUSO_PADRAO } from "@/lib/tempo";
import { agruparPedidosPorDia, serieDeAtividadeDiaria, type AtividadeDoDia } from "@/lib/agrupar-por-dia";
import { revalidatePath, revalidateTag, unstable_cache } from "next/cache";
import { redirect } from "next/navigation";
import bcrypt from "bcryptjs";
import { cookies } from "next/headers";
import { auth } from "@/lib/auth";
import { prisma } from "@/lib/prisma";
import { redigirCorpo } from '@/lib/redact-secrets';
import { SystemMonitorService } from "@/services/system-monitor.service";
import { ChatwootPlatformService } from "@/services/chatwoot-platform.service";
import { subDays, startOfDay, endOfDay } from "date-fns";
import * as PrismaClient from "@prisma/client";
import { BillingService } from "@/services/billing.service";
import { SubscriptionCompositionService } from "@/services/subscription-composition.service";
import { EntitlementService } from "@/services/entitlement.service";
import { LimitService } from "@/services/limit.service";
import { ErrorLogService } from "@/services/error-log.service";
import type { ErrorEventType } from "@/lib/error-log";
import { checkPermission, isFullAdmin, isPlatformUser } from "@/lib/rbac";
import { canMutatePlatformUser } from "@/lib/platform-user-guard";
import { encryptInfraToken } from "@/utils/crypto";
import { z } from "zod";
import { redis } from "@/lib/redis";
import { withTenant } from "@/lib/with-tenant";
import { checkRateLimit } from "@/lib/rate-limiter";
import { UserService } from "@/services/user.service";
import { MARCA_DE_ADOCAO } from "@/domain/whatsapp/instance-provenance-policy";
import { STORE_LIST_SELECT } from "@/lib/store-list-query";
import { filtroEscopoParaWhere } from "@/lib/store-plan-scope";
import { resolvePlanDisplayName } from "@/lib/plan-features";
import { podeDesvincularLegado, planejarRealinhamentoAoPacote } from "@/domain/billing/services/subscription-consistency";
import { LEGACY_LIMIT_KEYS } from "@/domain/entitlements/legacy-limits-projection";
import { permissoesParaCargo } from "@/domain/platform-team/policies/escopo-de-permissao";

export type ActionResponse<T = any> = {
    success: boolean;
    data?: T;
    error?: string;
};

// Fallback para o enum Plan
const Plan = (PrismaClient as any).Plan || {
    BASIC: "BASIC",
    PROFESSIONAL: "PROFESSIONAL",
};

/**
 * Guard das operações do painel super admin.
 *
 * - ADMIN full (wildcard) → passa sempre.
 * - Membro da equipe da plataforma (isPlatformStaff) → passa SE possuir `requiredPermission`.
 * - **Fail-closed**: sem `requiredPermission`, exige ADMIN full (qualquer action não-migrada nega STAFF).
 */
export async function getAdminSession(requiredPermission?: string) {
    const session = await auth();

    if (!session || !session.user) {
        throw new Error("Não autenticado.");
    }

    if (!isPlatformUser(session)) {
        throw new Error("Acesso negado. Requer acesso ao painel da plataforma.");
    }

    // ADMIN full bypassa qualquer checagem granular.
    if (isFullAdmin(session)) {
        return session;
    }

    // Equipe (STAFF): exige permissão específica; sem slug ⇒ somente ADMIN (fail-closed).
    if (!requiredPermission) {
        throw new Error("Acesso negado. Requer Administrador Global.");
    }
    if (!checkPermission(session, requiredPermission)) {
        throw new Error(`Acesso negado. Permissão '${requiredPermission}' necessária.`);
    }

    return session;
}

/** Alias semântico para gates por permissão de plataforma. */
export async function requireSuperAdminPermission(permission: string) {
    return getAdminSession(permission);
}

export async function getGlobalErrorLogsAction(): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.errors.view');

        // [R74] (03/09/2026): `select` nominal (o diálogo lê id, action, createdAt, store e details) e
        // `details` REDIGIDO por chave e por valor antes de descer ao browser — `auditLog.details` é o
        // destino que PERSISTE corpo de erro de terceiro (`docs/SECURITY-corpo-de-erro-cru.md`).
        // Medido no banco de teste: as linhas de erro só têm a chave `reason`, 0 sequências de 32+.
        const logs = await prisma.auditLog.findMany({
            where: {
                OR: [
                    { action: { contains: 'FAIL' } },
                    { action: { contains: 'ERROR' } }
                ]
            },
            take: 100,
            orderBy: { createdAt: 'desc' },
            select: {
                id: true,
                action: true,
                createdAt: true,
                storeId: true,
                details: true,
                store: { select: { name: true, slug: true } },
            },
        });

        return {
            success: true,
            data: logs.map((l) => ({ ...l, details: redigirCorpo(l.details, { textoLivre: 'escrubar' }) })),
        };

    } catch (error) {
        // Frase fixa: `error.message` do Prisma carrega valores da query.
        console.error('[SUPER-ADMIN] getGlobalErrorLogsAction:', error instanceof Error ? error.message : error);
        return { success: false, error: 'Erro ao carregar os registros de erro' };
    }
}

/** Lista paginada de eventos de erro/404 (observabilidade) para o painel Super Admin. */
export async function listErrorEventsAction(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    eventType?: ErrorEventType;
}): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.errors.view');
        const data = await ErrorLogService.list({
            page: params.page,
            pageSize: params.pageSize ?? 20,
            search: params.search?.trim() || undefined,
            eventType: params.eventType,
        });
        return { success: true, data };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

/** Limpa eventos de erro/404 mais antigos que `days` (padrão 90). Manutenção/retenção. */
export async function purgeOldErrorEventsAction(days: number = 90): Promise<ActionResponse> {
    try {
        await getAdminSession();
        const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        const result = await prisma.errorEvent.deleteMany({
            where: { createdAt: { lt: cutoff } },
        });
        revalidatePath("/admin/super/errors");
        return { success: true, data: { deleted: result.count } };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}



const getExchangeRates = unstable_cache(
    async () => {
        try {
            const res = await fetch("https://economia.awesomeapi.com.br/json/last/USD-BRL,EUR-BRL,MXN-BRL", {
                next: { revalidate: 3600 } // Fail-safe if cache wrapper misses
            });

            if (!res.ok) {
                console.warn("⚠️ AwesomeAPI de Câmbio retornou erro:", res.status);
                return { EUR: 6.20, USD: 5.75, MXN: 0.30, BRL: 1.0 };
            }

            const data = await res.json();
            // console.log("DEBUG: AwesomeAPI Response:", data);

            return {
                EUR: Number(data.EURBRL.bid),
                USD: Number(data.USDBRL.bid),
                MXN: Number(data.MXNBRL.bid),
                BRL: 1.0
            };
        } catch (error) {
            console.error("❌ Erro ao buscar câmbio, usando fallback:", error);
            return { EUR: 6.20, USD: 5.75, MXN: 0.30, BRL: 1.0 };
        }
    },
    ['exchange-rates-v1'],
    { revalidate: 3600 }
);

// --- Types ---



export type CreateStoreDTO = {
    name: string;
    slug: string;
    planId?: string;
    isAutoSyncEnabled?: boolean;
    /** 9d: como conceder a assinatura inicial. Ausente → LEGACY_PLAN (compat); o dialog envia TRIAL_PACKAGE. */
    grantMode?: "TRIAL_PACKAGE" | "COMPED_PACKAGE" | "LEGACY_PLAN";
    /** Pacote a conceder (modos modulares). Vazio → pacote padrão (Package.isDefault). */
    packageId?: string;
    /** Dias de trial (modo TRIAL_PACKAGE). Vazio → Package.trialDays. */
    trialDays?: number;
};

export type CreateUserDTO = {
    name: string;
    email: string;
    password: string;
    storeId: string;
    roleType: "STORE_OWNER" | "STAFF";
};

export type UpdateUserDTO = {
    id: string;
    name?: string;
    email?: string;
    password?: string;
    role?: UserRole;
    isHiddenFromReports?: boolean;
};

// --- Helpers ---



// --- Store Actions ---

export async function createStoreAction(data: CreateStoreDTO): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        // Check if slug exists
        const existing = await prisma.store.findUnique({ where: { slug: data.slug } });
        if (existing) {
            return { success: false, error: "Slug da loja já existe." };
        }

        // Default da AÇÃO = legado (compat p/ qualquer caller que envie só planId). O dialog do super-admin
        // pré-seleciona TRIAL_PACKAGE na UI e sempre envia grantMode explícito.
        const mode = data.grantMode ?? "LEGACY_PLAN";

        // ── 9d: concessão MODULAR (pacote) — trial sem cartão OU cortesia (comped) ────────────────
        if (mode === "TRIAL_PACKAGE" || mode === "COMPED_PACKAGE") {
            // Pacote escolhido (resolveComposition) OU pacote padrão (resolveDefaultPackageGrant).
            const composition = data.packageId
                ? await SubscriptionCompositionService
                    .resolveComposition({ packageId: data.packageId }, "MONTHLY")
                    .catch(() => null)
                : await SubscriptionCompositionService.resolveDefaultPackageGrant();

            if (!composition) {
                return { success: false, error: "Nenhum pacote disponível para concessão (escolha um pacote ou marque um pacote padrão na aba Pacotes)." };
            }

            const isComped = mode === "COMPED_PACKAGE";
            const trialDays = isComped ? 0 : Math.max(0, data.trialDays ?? composition.trialDays ?? 0);
            const isTrial = !isComped && trialDays > 0;

            // Transação Atômica: Store + Subscription modular + SubscriptionModule ativos (sem gateway).
            const result = await prisma.$transaction(async (tx: any) => {
                const store = await tx.store.create({
                    data: {
                        name: data.name,
                        slug: data.slug,
                        isAutoSyncEnabled: data.isAutoSyncEnabled ?? false,
                    }
                });

                const now = new Date();
                const trialEnd = new Date();
                if (isTrial) trialEnd.setDate(trialEnd.getDate() + trialDays);

                const subscription = await tx.subscription.create({
                    data: {
                        storeId: store.id,
                        packageId: composition.packageId,
                        billingInterval: composition.interval,
                        status: isTrial ? "TRIALING" : "ACTIVE",
                        trialEndsAt: isTrial ? trialEnd : null,
                        currentPeriodStart: now,
                        currentPeriodEnd: isTrial ? trialEnd : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000),
                    }
                });
                await SubscriptionCompositionService.reconcileModules(tx, subscription.id, composition.items);
                return { store, subscription };
            });

            try { await EntitlementService.invalidate(result.store.id); } catch { /* cache best-effort */ }
            console.log(`[STORE] Loja criada (modular ${isComped ? "cortesia" : "trial"}): ${result.store.name} (ID: ${result.store.id})`);
            revalidatePath("/admin/super/stores");
            // 🔴 NÃO devolver o registro do Prisma. `result.store` é a linha inteira de `Store`
            // (62 colunas) e `create-store-dialog.tsx:114-136` usa só `success`/`error` —
            // conferido. Travado por `linha-crua-no-cliente.test.ts` (eixo [B]).
            return { success: true, data: { id: result.store.id } };
        }

        // ── LEGADO: plano único (comportamento atual — intacto) ───────────────────────────────────
        let targetPlanId = data.planId;
        if (!targetPlanId) {
            const defaultPlan = await prisma.plan.findFirst({ where: { isDefault: true } });
            targetPlanId = defaultPlan?.id;
        }

        if (!targetPlanId) {
            return { success: false, error: "Nenhum plano configurado no sistema." };
        }

        const plan = await prisma.plan.findUnique({
            where: { id: targetPlanId },
            include: { limits: true }
        });

        if (!plan) {
            return { success: false, error: "Plano selecionado não encontrado." };
        }

        // 2. Transação Atômica: Store + Subscription
        const result = await prisma.$transaction(async (tx: any) => {
            const store = await tx.store.create({
                data: {
                    name: data.name,
                    slug: data.slug,
                    isAutoSyncEnabled: data.isAutoSyncEnabled ?? false,
                }
            });

            const now = new Date();
            const nextMonth = new Date();
            nextMonth.setMonth(now.getMonth() + 1);

            const subscription = await tx.subscription.create({
                data: {
                    storeId: store.id,
                    planId: targetPlanId as string,
                    status: "ACTIVE",
                    currentPeriodStart: now,
                    currentPeriodEnd: nextMonth,
                }
            });

            return { store, subscription };
        });

        console.log(`[STORE] Loja criada com sucesso: ${result.store.name} (ID: ${result.store.id})`);

        revalidatePath("/admin/super/stores");
        // 🔴 Mesma classe do sítio acima: allow-list, nunca a linha.
        return { success: true, data: { id: result.store.id } };

    } catch (error: any) {
        console.error("Erro ao criar loja:", error);
        return { success: false, error: error.message };
    }
}


/**
 * Remove o vínculo com o PLANO LEGADO de uma assinatura que já tem pacote — mão única.
 *
 * ## Por que uma AÇÃO e não um seletor de precedência
 *
 * Preferência permanente institucionaliza a ambiguidade; a ação converge e acaba com ela. E
 * "qual prevalece" não tem semântica definida: para as 6 lojas com os dois, o pacote é o que elas
 * TÊM (módulos ativos, entitlements resolvidos deles) e o legado é resíduo.
 *
 * ## O que NÃO muda
 *
 * O preço. Ele está congelado nos `SubscriptionModule.priceCents` — o backfill alocou o MRR
 * legado ali de propósito. Desvincular apaga o ponteiro, não a cobrança.
 *
 * ## A guarda que importa
 *
 * `podeDesvincularLegado` é do DOMÍNIO e é reavaliada AQUI, no servidor: o botão desabilitado é
 * sugestão de UI, não guarda. Enquanto `atendimento.rooms` não existir, a loja ficaria sem fonte
 * para o limite de salas.
 */
export async function desvincularPlanoLegadoAction(storeId: string): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.manage');

        // Zod na borda — o id vem de um componente de client.
        const { storeId: loja } = z.object({ storeId: z.string().min(1) }).parse({ storeId });

        const [sub, defDeSalas] = await Promise.all([
            prisma.subscription.findUnique({
                where: { storeId: loja },
                // `plan.basePrice` entra porque a âncora sai DELE — ver abaixo.
                select: { id: true, planId: true, packageId: true, plan: { select: { basePrice: true } } },
            }),
            prisma.entitlementDef.findUnique({
                where: { key: LEGACY_LIMIT_KEYS.maxRooms },
                select: { id: true },
            }),
        ]);

        if (!sub) return { success: false, error: 'Esta loja não tem assinatura.' };
        if (!sub.packageId) {
            // Sem pacote, o legado é a ÚNICA fonte — desvincular deixaria a loja sem plano algum.
            return { success: false, error: 'Esta loja não tem pacote; o plano legado é a única fonte.' };
        }

        const veredito = podeDesvincularLegado({
            planId: sub.planId,
            temEntitlementDeSalas: defDeSalas !== null,
        });
        if (!veredito.permitido) return { success: false, error: veredito.motivo };

        // 🔴 GRAVA A ÂNCORA ANTES DE ANULAR — a operação tem de ser SEM PERDA.
        //
        // Defeito real de 26/08: a primeira versão desta ação só anulava o `planId`. Mas era ele
        // que carregava a explicação do preço (a soma dos módulos preserva o MRR legado, não o
        // preço do pacote). A Companheiro Peludo, perfeitamente explicada, virou 🔴 "EXIGE
        // VERIFICAÇÃO" no instante do clique — indistinguível da única loja que precisa de
        // atenção de verdade. O botão fabricou um alarme falso.
        //
        // Mesma escrita, uma transação: não existe janela em que o plano sumiu e a âncora não
        // chegou. E `??` preserva âncora já gravada — não sobrescreve memória mais antiga.
        const ancoraCents =
            sub.plan?.basePrice != null ? Math.round(Number(sub.plan.basePrice) * 100) : null;
        await prisma.subscription.update({
            where: { id: sub.id },
            data: {
                planId: null,
                ...(ancoraCents !== null ? { legacyPriceAnchorCents: ancoraCents } : {}),
            },
        });

        // Import tardio, como o resto deste arquivo faz: `audit.service` puxa `@/lib/prisma` e
        // `next/headers`, e carregá-lo no topo arrastaria isso para todo consumidor da action.
        const { AuditService } = await import("@/services/audit.service");
        await AuditService.log({
            userId: session.user?.id,
            userEmail: session.user?.email ?? undefined,
            action: 'UPDATE',
            resource: 'SUBSCRIPTION',
            resourceId: sub.id,
            storeId: loja,
            details: {
                evento: 'plano_legado_desvinculado',
                planIdAnterior: sub.planId,
                packageId: sub.packageId,
                ancoraDePrecoCents: ancoraCents,
            },
        });

        // O mapa de entitlements muda de ramo — sem isto o cache serviria o antigo por 10 min.
        await EntitlementService.invalidate(loja);

        revalidatePath('/admin/super/stores');
        return { success: true };
    } catch (error: any) {
        console.error('Erro ao desvincular plano legado:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Realinha o preço da assinatura ao preço de TABELA do pacote — desfaz o grandfathering que o
 * backfill aplicou a quem nunca foi cobrado.
 *
 * 🔴 **Não existe caminho de "forçar" por aqui, e é decisão.** Assinatura com gateway vivo é
 * recusada, ponto: mudar o preço só no banco faria o sistema exibir um valor e o cliente pagar
 * outro no extrato — divergência pior que a corrigida, e invisível. Quem precisa fazer isso
 * assume o risco no terminal, com `scripts/realinhar-preco-ao-pacote.ts --forcar-gateway`, depois
 * de atualizar a assinatura no gateway. Um botão de forçar no painel transformaria uma decisão
 * financeira deliberada em dois cliques.
 *
 * A pré-condição é do DOMÍNIO (`planejarRealinhamentoAoPacote`) e é reavaliada aqui do lado do
 * servidor — o que a UI mostra é sugestão, nunca guarda.
 */
export async function realinharPrecoAoPacoteAction(storeId: string): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.manage');

        // Zod na borda — o id vem de um componente de client.
        const { storeId: loja } = z.object({ storeId: z.string().min(1) }).parse({ storeId });

        const sub = await prisma.subscription.findUnique({
            where: { storeId: loja },
            select: {
                id: true,
                billingInterval: true,
                legacyPriceAnchorCents: true,
                gatewaySubscriptionId: true,
                package: { select: { priceMonthlyCents: true, priceYearlyCents: true } },
                // Ordem estável: o rateio devolve os preços NA MESMA ORDEM dos pesos enviados.
                modules: { where: { active: true }, orderBy: { id: 'asc' }, select: { id: true, priceCents: true } },
            },
        });

        if (!sub) return { success: false, error: 'Esta loja não tem assinatura.' };

        const packagePriceCents =
            (sub.billingInterval === 'YEARLY'
                ? sub.package?.priceYearlyCents
                : sub.package?.priceMonthlyCents) ?? null;
        const pesos = sub.modules.map((m) => m.priceCents);
        const somaAntes = pesos.reduce((a, b) => a + b, 0);

        const veredito = planejarRealinhamentoAoPacote({
            packagePriceCents,
            pesosDosModulosCents: pesos,
            temGatewayVivo: sub.gatewaySubscriptionId != null && sub.gatewaySubscriptionId !== '',
            // Ausente de propósito — ver o bloco acima.
        });
        if (!veredito.permitido) return { success: false, error: veredito.motivo };

        await prisma.$transaction(async (tx) => {
            for (const [i, mod] of sub.modules.entries()) {
                // Escrita pelo ID da PRÓPRIA linha carregada — nunca por busca de preço.
                await tx.subscriptionModule.update({
                    where: { id: mod.id },
                    data: { priceCents: veredito.novosPrecosCents[i] },
                });
            }
            // A âncora morre junto com a divergência: ela existe para EXPLICAR um valor que não
            // bate com o pacote, e sem divergência explicaria algo que não existe mais.
            await tx.subscription.update({
                where: { id: sub.id },
                data: { legacyPriceAnchorCents: null },
            });
        });

        // Import tardio, como o resto deste arquivo faz: `audit.service` puxa `@/lib/prisma` e
        // `next/headers`, e carregá-lo no topo arrastaria isso para todo consumidor da action.
        const { AuditService } = await import("@/services/audit.service");
        await AuditService.log({
            userId: session.user?.id,
            userEmail: session.user?.email ?? undefined,
            action: 'UPDATE',
            resource: 'SUBSCRIPTION',
            resourceId: sub.id,
            storeId: loja,
            details: {
                evento: 'preco_realinhado_ao_pacote',
                somaAnteriorCents: somaAntes,
                somaNovaCents: packagePriceCents,
                ancoraApagadaCents: sub.legacyPriceAnchorCents,
            },
        });

        // Sem isto o preço/limites seguiriam em cache por 10 min.
        await EntitlementService.invalidate(loja);

        revalidatePath('/admin/super/stores');
        return { success: true };
    } catch (error: any) {
        console.error('Erro ao realinhar preço ao pacote:', error);
        return { success: false, error: error.message };
    }
}

export async function listStoresAction(filters?: {
    search?: string;
    /** Escopo de plano — os quatro são MUTUAMENTE EXCLUSIVOS (ver `store-plan-scope.ts`). */
    planId?: string;
    packageId?: string;
    moduleLevelId?: string;
    composicao?: string;
    status?: string;
    region?: string;
    from?: string;
    to?: string;
    minOrders?: string;
    maxOrders?: string;
    sort?: string;
    order?: "asc" | "desc";
    page?: number;
    pageSize?: number;
}): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.view');

        const now = new Date();

        // 1. Build Prisma Where Clause
        const where: any = {};
        // Excluir a loja-plataforma (Mulply interna) da gestão de lojas-cliente.
        where.isPlatform = false;

        if (filters?.search) {
            where.OR = [
                { name: { contains: filters.search, mode: 'insensitive' } },
                { slug: { contains: filters.search, mode: 'insensitive' } },
                {
                    userStores: {
                        some: {
                            user: {
                                email: { contains: filters.search, mode: 'insensitive' }
                            }
                        }
                    }
                }
            ];
        }

        // Escopo de plano: Legado (`planId`) · Pacote (`packageId`) · Pacote Montado
        // (`composicao=montada`) · Módulo (`moduleLevelId`).
        //
        // 🔴 O filtro consultava SÓ `planId`, e o seletor só oferecia planos legados. Medido em
        // 26/08/2026: das 18 assinaturas, **7** têm plano legado (6 delas com pacote junto) e
        // **11 não eram alcançáveis por opção nenhuma** — são as que só têm pacote. Sem erro,
        // sem aviso: a lista simplesmente vinha vazia.
        //
        // ⚠️ O SPREAD não é cosmético. O switch de status abaixo também escreve em
        // `where.billingSubscription`; sem ele isto era seguro apenas por ORDEM, e o próximo
        // filtro que alguém escrevesse acima do switch sumiria em silêncio.
        const escopoPlano = filtroEscopoParaWhere({
            planId: filters?.planId ?? null,
            packageId: filters?.packageId ?? null,
            moduleLevelId: filters?.moduleLevelId ?? null,
            composicao: filters?.composicao ?? null,
        });
        if (escopoPlano) {
            where.billingSubscription = { ...where.billingSubscription, ...escopoPlano };
        }

        if (filters?.region && filters.region !== "all") {
            where.region = filters.region;
        }

        if (filters?.from || filters?.to) {
            where.createdAt = {
                ...(filters.from ? { gte: new Date(filters.from) } : {}),
                ...(filters.to ? { lte: new Date(filters.to) } : {}),
            };
        }

        if (filters?.status && filters.status !== "all") {
            switch (filters.status) {
                case 'active':
                    where.billingSubscription = {
                        ...where.billingSubscription,
                        status: 'ACTIVE'
                    };
                    where.isUnderMaintenance = false;
                    break;
                case 'trial':
                    where.billingSubscription = {
                        ...where.billingSubscription,
                        status: 'TRIALING',
                        trialEndsAt: { gte: now }
                    };
                    break;
                case 'vencida': {
                    // 🔴 MUDANÇA DE FORMA da query — declarada de propósito (`storeid-rbac.md` §6.3).
                    //
                    // Antes: `where.OR = [...(where.OR || []), ...os 4 status]`. Como a busca
                    // (`:371`) também escreve `where.OR`, os dois grupos se FUNDIAM num único
                    // array — e um `OR` de 7 termos é UNIÃO: buscar "acme" e filtrar "vencida"
                    // devolvia todas as vencidas SOMADAS a todas que casavam "acme".
                    //
                    // Agora cada grupo vira seu próprio `{ OR: [...] }` dentro de `where.AND`,
                    // que é interseção. Só entra em `AND` quando há de fato colisão — com um
                    // filtro só a forma continua idêntica (há controle negativo no teste).
                    //
                    // ⚠️ `where.isPlatform` fica NO TOPO, nunca dentro do `AND`: é a exclusão da
                    // loja-plataforma, e lá dentro poderia ser deslocada por engano.
                    const grupoStatus = [
                        { billingSubscription: { status: 'PAST_DUE' } },
                        { billingSubscription: { status: 'TRIALING', trialEndsAt: { lt: now } } },
                        { billingSubscription: { status: 'UNPAID' } },
                        { billingSubscription: { status: 'CANCELED' } }
                    ];
                    if (where.OR) {
                        where.AND = [...(where.AND || []), { OR: where.OR }, { OR: grupoStatus }];
                        delete where.OR;
                    } else {
                        where.OR = grupoStatus;
                    }
                    break;
                }
                case 'maintenance':
                    where.isUnderMaintenance = true;
                    break;
            }
        }

        // Pagination and Sorting setup
        const page = filters?.page || 1;
        const pageSize = filters?.pageSize || 20;
        const skip = (page - 1) * pageSize;
        const take = pageSize;

        const sortField = filters?.sort || 'createdAt';
        const sortOrder = filters?.order || 'desc';

        // Check if we need memory-based pagination (Order volume filter)
        const isMemoryFilteringNeeded = !!(filters?.minOrders || filters?.maxOrders);

        // Sorting configuration with isPinned priority
        let orderBy: any = [];

        // ALWAYS prioritize pinned stores first
        orderBy.push({ isPinned: 'desc' });

        // Then apply user's sort preference
        if (['createdAt', 'name', 'slug'].includes(sortField)) {
            orderBy.push({ [sortField]: sortOrder });
        } else {
            // Default sort if field not directly in Store model or needs special handling
            orderBy.push({ createdAt: 'desc' });
        }

        // Define timeframe for health check (12h)
        const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // 🔴 `select` explícito, NUNCA `include`. Sem projeção, o Prisma devolve as 62 colunas
        // de `Store` — `chatwootSsoSecret`, `chatwootSsoKey`, `openaiApiKey`, `document`
        // (CNPJ/CPF do lojista), `contactEmail`, `contactPhone`, `address`, `rawPayload` — e a
        // linha inteira de `Subscription` (30 colunas, com os identificadores de gateway e do
        // cartão), tudo até `<StoresClientWrapper>`, que é `"use client"`.
        //
        // `where`, `orderBy`, `skip` e `take` ficam INTOCADOS: mudar a forma da query é mudança
        // de segurança (`storeid-rbac.md` §6.3), e aqui só a projeção muda.
        // Travado por `linha-crua-no-cliente.test.ts` e `list-stores-projection.test.ts`.
        const stores = await prisma.store.findMany({
            where,
            orderBy: isMemoryFilteringNeeded ? undefined : orderBy,
            skip: isMemoryFilteringNeeded ? undefined : skip,
            take: isMemoryFilteringNeeded ? undefined : take,
            select: STORE_LIST_SELECT,
        });

        // Get total count for non-memory pagination
        let totalCount = 0;
        if (!isMemoryFilteringNeeded) {
            totalCount = await prisma.store.count({ where });
        }

        // Optimized Health Check
        const healthLogs = await prisma.auditLog.groupBy({
            by: ['storeId', 'action'],
            where: {
                createdAt: { gte: twelveHoursAgo },
                action: { startsWith: 'WEBHOOK_' }
            },
            _count: {
                _all: true
            }
        });

        // Get conversion rates (paid orders / total orders) last 30 days
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        const conversionData = await prisma.order.groupBy({
            by: ['storeId', 'status'],
            where: {
                createdAt: { gte: thirtyDaysAgo }
            },
            _count: {
                _all: true
            }
        });

        // Get orders per day (last 7 days average)
        const ordersLast7Days = await prisma.order.groupBy({
            by: ['storeId'],
            where: {
                createdAt: { gte: sevenDaysAgo }
            },
            _count: {
                _all: true
            }
        });

        // Get last sync timestamp
        const lastSyncs = await prisma.auditLog.groupBy({
            by: ['storeId'],
            where: {
                action: { startsWith: 'WEBHOOK_' },
                createdAt: { gte: twelveHoursAgo }
            },
            _max: {
                createdAt: true
            }
        });

        // 9g: limites modular-aware de TODAS as lojas em 1 query (sem N+1) → projetados em memória.
        const storeLimitsMap = await LimitService.getStoreLimitsBatch(stores.map((s: any) => s.id));

        // Map logs to stores and calculate extra metrics
        let storesWithHealth = stores.map((store: any) => {
            const storeLogs = healthLogs.filter((l: any) => l.storeId === store.id);
            const total = storeLogs.reduce((acc: number, curr: any) => acc + curr._count._all, 0);

            const failures = storeLogs
                .filter((l: any) => l.action.includes('FAIL') || l.action.includes('ERROR'))
                .reduce((acc: number, curr: any) => acc + curr._count._all, 0);

            const failureRate = total > 0 ? (failures / total) : 0;
            const isHealthy = failureRate < 0.05; // Less than 5% failure

            // Calculate conversion rate
            const storeConversion = conversionData.filter((c: any) => c.storeId === store.id);
            const totalOrdersInRange = storeConversion.reduce((acc: number, curr: any) => acc + curr._count._all, 0);
            const paidOrders = storeConversion
                .filter((c: any) => c.status === 'PAID' || (c.status as string) === 'DELIVERED')
                .reduce((acc: number, curr: any) => acc + curr._count._all, 0);
            const conversionRate = totalOrdersInRange > 0 ? (paidOrders / totalOrdersInRange) * 100 : 0;

            // Calculate orders per day (7-day average)
            const ordersCount = ordersLast7Days.find((o: any) => o.storeId === store.id)?._count._all || 0;
            const ordersPerDay = ordersCount / 7;

            // Last sync
            const lastSync = lastSyncs.find((s: any) => s.storeId === store.id)?._max.createdAt;

            const subscription = store.billingSubscription;
            // 9g: limites via projeção modular-aware (batch, sem N+1). Fallback defensivo se faltar no map.
            const lim = storeLimitsMap.get(store.id);

            const maxOrders = lim?.maxOrdersPerMonth ?? 0;
            const usagePercentage = maxOrders > 0 ? Math.round((store._count.orders / maxOrders) * 100) : 0;

            // Upsell indicators
            const maxUsers = lim?.maxUsers ?? 0;
            const maxWhatsapp = lim?.maxWhatsappConnections ?? 1;
            const maxEcommerce = lim?.maxEcommerceIntegrations ?? 1;

            const currentUsers = store._count?.userStores ?? 0;
            const currentWhatsapp = store._count?.whatsappConnections ?? 0;
            const currentEcommerce = store._count?.integrations ?? 0;

            const usersUsage = maxUsers > 0 ? (currentUsers / maxUsers) * 100 : 0;
            const whatsappUsage = maxWhatsapp > 0 ? (currentWhatsapp / maxWhatsapp) * 100 : 0;
            const ecommerceUsage = maxEcommerce > 0 ? (currentEcommerce / maxEcommerce) * 100 : 0;

            const isUpsellCritical = usersUsage >= 100 || whatsappUsage >= 100 || ecommerceUsage >= 100;
            const isUpsellWarning = usersUsage >= 80 || whatsappUsage >= 80 || ecommerceUsage >= 80;

            const serializedStore = JSON.parse(JSON.stringify(store));

            return {
                ...serializedStore,
                healthStatus: isHealthy ? 'ONLINE' : 'WARNING',
                healthDetails: { total, failures, failureRate },
                conversionRate: Math.round(conversionRate * 10) / 10,
                ordersPerDay: Math.round(ordersPerDay * 10) / 10,
                lastSync,
                // 🔴 O PACOTE VEM PRIMEIRO. Seis das 18 assinaturas têm plano legado **e**
                // pacote; com `plan` na frente elas exibiam "Enterprise" quando o pacote real
                // era "Mulply Scale". O legado continua como fallback para a assinatura que só
                // tem ele, e 'Composição modular' cobre a assinatura à la carte (sem os dois).
                planName: subscription
                    ? resolvePlanDisplayName({
                          packageName: subscription.package?.name,
                          legacyPlanName: subscription.plan?.name,
                          fallback: 'Composição modular',
                      })
                    : 'Sem Plano',
                // A cor acompanha o NOME, senão o badge diria "Mulply Scale" pintado com a cor
                // do "Enterprise".
                planColor: subscription?.package?.color || subscription?.plan?.color || '#3b82f6',
                billingStatus: subscription?.status || 'INACTIVE',
                usage: {
                    percentage: usagePercentage,
                    isCritical: usagePercentage > 90
                },
                usageLimits: {
                    users: { current: currentUsers, max: maxUsers, percentage: Math.round(usersUsage) },
                    whatsapp: { current: currentWhatsapp, max: maxWhatsapp, percentage: Math.round(whatsappUsage) },
                    ecommerce: { current: currentEcommerce, max: maxEcommerce, percentage: Math.round(ecommerceUsage) }
                },
                upsellStatus: isUpsellCritical ? 'CRITICAL' : isUpsellWarning ? 'WARNING' : 'OK'
            };
        });

        // 5. Batch Telemetry (Redis Pipeline)
        const storeIds = storesWithHealth.map((s: any) => s.id);
        const cutoff = Date.now() - 120_000;
        const telemetryPipe = redis.pipeline();

        storeIds.forEach((id: string) => {
            const onlineKey = `online:${id}`;
            telemetryPipe.zremrangebyscore(onlineKey, "-inf", cutoff);
            telemetryPipe.zcard(onlineKey);
        });

        const startDate = subDays(new Date(), 7);
        startDate.setHours(0, 0, 0, 0);

        const [telemetryResults, engagementStats] = await Promise.all([
            // `.catch` obrigatório: sem ele, o Redis fora do ar rejeita, derruba este
            // `Promise.all` inteiro — levando junto o groupBy de engajamento — e a listagem de
            // lojas MORRE. Encadeamento opcional na leitura não cobre, porque a rejeição
            // acontece antes de haver o que ler.
            telemetryPipe.exec().catch(() => null),
            prisma.userActivityDaily.groupBy({
                by: ['storeId'],
                where: {
                    storeId: { in: storeIds },
                    date: { gte: startDate }
                },
                _sum: {
                    totalSeconds: true
                }
            })
        ]);

        // 🔴 Telemetria indexada por `storeId`, não por posição.
        //
        // Antes: `telemetryResults[index * 2 + 1][1]`. Dois problemas no mesmo acesso:
        //   (a) sem guarda — resposta mais curta que 2N (falha parcial do pipeline) lê `[1]` de
        //       `undefined` e derruba a LISTAGEM INTEIRA com erro genérico;
        //   (b) era o único agregado casado por POSIÇÃO; os três vizinhos casam por chave
        //       (`.find(e => e.storeId === store.id)`). Correto enquanto nada reordena entre a
        //       montagem de `storeIds` e a leitura — no dia em que alguém inserir um `filter` ou
        //       `sort` nesse intervalo, a contagem de uma loja apareceria na linha de outra, sem
        //       erro e sem teste vermelho.
        //
        // O `as number | null | undefined` é necessário: `exec()` devolve
        // `[error: Error | null, result: unknown][] | null`, e sem o cast `onlineUsers` deixa de
        // ser `number`.
        const onlinePorLoja = new Map<string, number>(
            storeIds.map((id: string, i: number) => [
                id,
                (telemetryResults?.[i * 2 + 1]?.[1] as number | null | undefined) ?? 0,
            ])
        );

        storesWithHealth = storesWithHealth.map((store: any) => {
            const onlineCount = onlinePorLoja.get(store.id) ?? 0;
            const storeEngagement = engagementStats.find(e => e.storeId === store.id);
            const totalSec = storeEngagement?._sum.totalSeconds || 0;
            const avgDailyHours = Number((totalSec / 7 / 3600).toFixed(1));

            return {
                ...store,
                onlineUsers: onlineCount,
                avgUsage7d: avgDailyHours
            };
        });

        // ──────────────────────────────────────────────────────────────────────────────
        // Memory Filtering (Order Volume)
        // ──────────────────────────────────────────────────────────────────────────────
            if (isMemoryFilteringNeeded) {
                const min = filters?.minOrders ? parseInt(filters.minOrders) : 0;
                const max = filters?.maxOrders ? parseInt(filters.maxOrders) : Infinity;

                storesWithHealth = storesWithHealth.filter((s: any) => {
                    const count = s._count?.orders || 0;
                    return count >= min && count <= max;
                });

                totalCount = storesWithHealth.length;

                // Apply Memory Sorting if needed
                if (!['createdAt', 'name', 'slug'].includes(sortField)) {
                    storesWithHealth.sort((a: any, b: any) => {
                        let valA: any = 0;
                        let valB: any = 0;

                    if (sortField === 'usage') {
                        valA = a.usage?.percentage || 0;
                        valB = b.usage?.percentage || 0;
                    } else if (sortField === 'orders') {
                        valA = a._count?.orders || 0;
                        valB = b._count?.orders || 0;
                    }

                    return sortOrder === 'asc' ? valA - valB : valB - valA;
                });
            }

            // Apply Memory Pagination
            storesWithHealth = storesWithHealth.slice(skip, skip + take);
        }

        return { success: true, data: { stores: storesWithHealth, totalCount } };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

/**
 * Lista mínima de lojas para SELETOR — `{ id, name }`, todas, sem paginação.
 *
 * ## Por que existe
 *
 * `/admin/super/users` chamava `listStoresAction()` sem argumento só para preencher o seletor
 * de loja do "Novo Operador". Duas consequências, ambas medidas:
 *
 *  1. **Teto silencioso de 20 lojas.** `pageSize` cai no default 20 (`listStoresAction`), então
 *     a partir da 21ª loja o super admin simplesmente não consegue atribuir usuário a ela — sem
 *     erro, sem aviso. O banco de teste tem exatamente 20 lojas, o que tornava o corte invisível.
 *  2. **Custo absurdo para dois campos.** A listagem completa faz agregações de pedidos, health
 *     check, pipeline de Redis e cálculo de limites — tudo descartado para exibir `id` e `name`.
 *
 * ## Sem `take`, de propósito
 *
 * Truncar em silêncio é exatamente o defeito sendo corrigido. Com `{id,name}` o payload é
 * pequeno mesmo em centenas de lojas; o que não escala é a UI, e por isso os dois consumidores
 * ganharam campo de busca no mesmo commit.
 *
 * ## Guarda: `platform.stores.view`, a mesma de `listStoresAction`
 *
 * Byte a byte o que já era exigido hoje — esta action **não** amplia superfície de autorização.
 * Alargar para aceitar `platform.users.manage` foi considerado e descartado: faria um staff sem
 * `platform.stores.view` passar a enumerar o nome de TODAS as lojas, o que hoje ele não vê
 * (`users/page.tsx` mostra só as lojas DE CADA usuário). Mais permissivo não é mais completo.
 *
 * ## `try/catch` obrigatório
 *
 * O molde óbvio (`listStoresBasic` em `tracking-providers.ts`) devolve array nu e deixa o
 * `throw` subir. Aqui a chamada vive dentro de um `Promise.all` numa página; um throw derrubaria
 * a tela inteira de Usuários para o `error.tsx`. Devolver `ActionResponse` degrada, não quebra —
 * e a UI distingue "não há lojas" de "não consegui carregar".
 */
export async function listStoresPickerAction(): Promise<ActionResponse<{ id: string; name: string }[]>> {
    try {
        await getAdminSession('platform.stores.view');
        const lojas = await prisma.store.findMany({
            where: { isPlatform: false },
            select: { id: true, name: true },
            orderBy: { name: 'asc' },
        });
        return { success: true, data: lojas };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function toggleMaintenanceAction(storeId: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        const store = await prisma.store.findUnique({ where: { id: storeId } });
        if (!store) return { success: false, error: "Loja não encontrada." };

        await prisma.store.update({
            where: { id: storeId },
            data: { isUnderMaintenance: !store.isUnderMaintenance }
        });

        revalidatePath("/admin/super/stores");
        // 🔴 NÃO devolver o registro do Prisma. `ActionResponse.data` é serializado e enviado
        // ao browser (o consumidor é `"use client"`), e a linha crua carrega colunas que nunca
        // podem cruzar essa fronteira. Os consumidores usam só `success`/`error` — conferido
        // arquivo a arquivo antes de estreitar. Travado por
        // `src/lib/__tests__/linha-crua-no-cliente.test.ts`.
        return { success: true };

    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function toggleStorePinAction(storeId: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        const store = await prisma.store.findUnique({ where: { id: storeId } });
        if (!store) return { success: false, error: "Loja não encontrada." };

        await prisma.store.update({
            where: { id: storeId },
            data: { isPinned: !store.isPinned }
        });

        revalidatePath("/admin/super/stores");
        // 🔴 NÃO devolver o registro do Prisma. `ActionResponse.data` é serializado e enviado
        // ao browser (o consumidor é `"use client"`), e a linha crua carrega colunas que nunca
        // podem cruzar essa fronteira. Os consumidores usam só `success`/`error` — conferido
        // arquivo a arquivo antes de estreitar. Travado por
        // `src/lib/__tests__/linha-crua-no-cliente.test.ts`.
        return { success: true };

    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function getStoreWeeklyEngagementAction(storeId: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');
        
        const now = new Date();
        const startDate = new Date();
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);

        const activities = await prisma.userActivityDaily.groupBy({
            by: ['date'],
            where: {
                storeId,
                date: {
                    gte: startDate
                }
            },
            _sum: {
                totalSeconds: true
            },
            orderBy: {
                date: 'asc'
            }
        });

        // Garantir preenchimento dos últimos 7 dias mesmo se não houver acesso num dia
        const last7Days = Array.from({ length: 7 }, (_, i) => {
            const d = new Date();
            d.setDate(now.getDate() - 6 + i); // 6 dias atrás até hoje
            return d;
        });

        // Chave e rótulo do MESMO dia UTC (`UserActivityDaily.date` é @db.Date em UTC) — antes a chave
        // era UTC e o rótulo o fuso do processo. Ver `src/lib/agrupar-por-dia.ts`.
        const data = serieDeAtividadeDiaria(last7Days, activities as unknown as AtividadeDoDia[]);

        return { success: true, data };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function getTotalOnlineUsersAction(): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');
        let cursor = "0";
        let totalUsers = 0;
        let onlineStoresCount = 0;
        const cutoff = Date.now() - 120_000;

        // Fail-safe wrapper for Redis operations
        try {
            do {
                const [nextCursor, keys] = await redis.scan(cursor, "MATCH", "online:*", "COUNT", 100);
                cursor = nextCursor;

                if (keys.length > 0) {
                    const pipe = redis.pipeline();
                    keys.forEach(key => {
                        pipe.zremrangebyscore(key, "-inf", cutoff);
                        pipe.zcard(key);
                    });
                    
                    const results = await pipe.exec();
                    if (results) {
                        results.forEach((res, index) => {
                            if (index % 2 === 1) { // ZCARD result
                                const card = res[1] as number;
                                if (card > 0) {
                                    totalUsers += card;
                                    onlineStoresCount++;
                                }
                            }
                        });
                    }
                }
            } while (cursor !== "0");
        } catch (redisErr) {
            console.error("Redis failure in getTotalOnlineUsersAction:", redisErr);
            // Return 0 silently instead of crashing (fail-safe)
            return { success: true, data: { totalUsers: 0, onlineStoresCount: 0 } };
        }

        return { success: true, data: { totalUsers, onlineStoresCount } };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function getGlobalWeeklyActivityAction(from?: string, to?: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');

        const now = new Date();

        let startDate: Date;
        let endDate: Date;

        if (from && to) {
            startDate = new Date(from);
            endDate = new Date(to);
        } else {
            endDate = new Date(now);
            startDate = subDays(now, 6);
            startDate.setHours(0, 0, 0, 0);
        }

        // Cap at 60 days to keep the response lean
        const diffMs = endDate.getTime() - startDate.getTime();
        const numDays = Math.min(Math.max(Math.ceil(diffMs / (1000 * 60 * 60 * 24)), 1), 60);

        const activities = await prisma.userActivityDaily.groupBy({
            by: ['date'],
            where: {
                date: { gte: startDate, lte: endDate }
            },
            _sum: {
                totalSeconds: true
            },
            orderBy: {
                date: 'asc'
            }
        });

        const daysArray = Array.from({ length: numDays }, (_, i) => {
            const d = new Date(startDate);
            d.setDate(d.getDate() + i);
            return d;
        });

        const data = serieDeAtividadeDiaria(daysArray, activities as unknown as AtividadeDoDia[]);

        return { success: true, data };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function bulkUpdateStatusAction(storeIds: string[], status: "ACTIVE" | "INACTIVE"): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        await prisma.store.updateMany({
            where: { id: { in: storeIds } },
            data: {
                // Note: The UI request said "status", but in our schema Maintenance 
                // is a separate boolean toggle. If they meant Billing Status, 
                // it's in the Subscription model. 
                // Given the instructions, I'll update isUnderMaintenance 
                // if they meant a general "status" toggle, but the prompt says 
                // bulkUpdateStatusAction(storeIds, newStatus). 
                // Let's check common status fields. 
                isUnderMaintenance: status === "INACTIVE"
            }
        });

        revalidatePath("/admin/super/stores");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function bulkExtendTrialAction(storeIds: string[], extraDays: number): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        // Guardrail: Only extend trial for stores that are TRIALING or PAST_DUE
        const subStatusToUpdate = ["TRIALING", "PAST_DUE"];

        const subscriptions = await prisma.subscription.findMany({
            where: {
                storeId: { in: storeIds },
                status: { in: subStatusToUpdate as any }
            },
            select: { id: true, trialEndsAt: true, currentPeriodEnd: true }
        });

        if (subscriptions.length === 0) {
            return { success: false, error: "Nenhuma loja elegível para extensão de trial encontrada." };
        }

        await prisma.$transaction(
            subscriptions.map((sub: any) => {
                const baseDate = sub.trialEndsAt || sub.currentPeriodEnd || new Date();
                const newDate = new Date(baseDate);
                newDate.setDate(newDate.getDate() + extraDays);

                return prisma.subscription.update({
                    where: { id: sub.id },
                    data: {
                        trialEndsAt: newDate,
                        status: "TRIALING" // Force back to trialing if it was past_due
                    }
                });
            })
        );

        revalidatePath("/admin/super/stores");
        return { success: true, data: { updatedCount: subscriptions.length } };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}









export async function testStoreConnectionAction(storeId: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        const integration = await prisma.integration.findFirst({
            where: { storeId, isActive: true },
            select: { provider: true, credentials: true, name: true }
        });

        if (!integration) {
            return { success: false, error: "Nenhuma integração ativa encontrada." };
        }

        const creds = integration.credentials as any;

        // Simulate Ping / Real Request based on Provider
        if (integration.provider === "SHOPIFY") {
            if (!creds?.shopName || !creds?.accessToken) {
                return { success: false, error: "Credenciais Shopify incompletas." };
            }
            try {
                // Real basic fetch to Shopify
                const response = await fetch(`https://${creds.shopName}/admin/api/2023-10/shop.json`, {
                    headers: {
                        'X-Shopify-Access-Token': creds.accessToken
                    }
                });
                if (!response.ok) throw new Error(`Shopify respondeu ${response.status}`);
            } catch (e: any) {
                return { success: false, error: `Falha Shopify: ${e.message}` };
            }

        } else if (integration.provider === "YAMPI") {
            if (!creds?.alias || !creds?.token) {
                return { success: false, error: "Credenciais Yampi incompletas." };
            }
            // Yampi doesn't have a simple public 'ping' without valid resource usually, 
            // but we can assume if we have creds, we are "connected" or try a light endpoint if known.
            // For now, let's validate existence of credentials as a "Local Check" plus a mock network delay
            // to simulate "Testing...".
            await new Promise(r => setTimeout(r, 800)); // Simulating network check
        }

        return { success: true, data: { message: `Conexão com ${integration.name} (${integration.provider}) OK!` } };

    } catch (error: any) {
        return { success: false, error: error.message };
    }
}


export async function getGlobalSaaSMetricsAction(from?: string, to?: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');

        const now = new Date();
        const startDate = from ? new Date(from) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 30, 0, 0, 0, 0));
        const endDate = to ? new Date(to) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));

        const CAMBIO = await getExchangeRates();

        // Financial Precision Filter: Primary is paidAt, Fallback is status PAID with createdAt
        // store.isDemo=false → lojas demo nunca entram nas métricas globais reais.
        const paidFilter = {
            store: { isDemo: false },
            OR: [
                { paidAt: { gte: startDate, lte: endDate } },
                {
                    status: { in: ['PAID', 'IN_TRANSIT', 'COMPLETED'] } as any,
                    paidAt: null,
                    createdAt: { gte: startDate, lte: endDate }
                }
            ]
        };

        // Parallelize all independent metrics queries
        const [
            totalStores,
            ordersInRange,
            totalOrdersInRange,
            totalRevenueAgg,
            activeStores,
            integrationIdRevenue,
            criticalOrdersCount,
            totalIntegrations,
            activeIntegrations,
            billingMetrics
        ] = await Promise.all([
            prisma.store.count({ where: { isDemo: false, isPlatform: false } }),
            prisma.order.count({ where: paidFilter }),
            prisma.order.count({ where: { store: { isDemo: false }, createdAt: { gte: startDate, lte: endDate } } }),
            prisma.order.aggregate({
                where: paidFilter,
                _sum: { totalBRL: true }
            }),
            prisma.subscription.count({ where: { status: 'ACTIVE', store: { isDemo: false } } }), // Stores with active subscription
            prisma.order.groupBy({
                by: ['integrationId'],
                where: paidFilter,
                _sum: { totalBRL: true },
                _count: { _all: true }
            }),
            prisma.order.count({
                where: {
                    store: { isDemo: false },
                    status: { in: ['PENDING'] },
                    createdAt: { gte: startDate, lte: endDate }
                }
            }),
            prisma.integration.count({ where: { store: { isDemo: false } } }),
            prisma.integration.count({ where: { isActive: true, store: { isDemo: false } } }),
            BillingService.getGlobalBillingMetrics(startDate, endDate)
        ]);

        const totalRevenue = Number(totalRevenueAgg._sum.totalBRL || 0);
        const integrationHealth = totalIntegrations > 0 ? Math.round((activeIntegrations / totalIntegrations) * 100) : 0;

        // Volume-First Chart Data: Show ALL orders created vs Revenue

        // 1. Get ALL orders for volume
        const allOrdersInRange = await prisma.order.findMany({
            where: { store: { isDemo: false }, createdAt: { gte: startDate, lte: endDate } },
            select: { createdAt: true }
        });

        // 2. Get PAID orders for revenue
        const paidOrdersInRange = await prisma.order.findMany({
            where: paidFilter,
            select: { totalBRL: true, createdAt: true, paidAt: true }
        });

        // O dia do pedido em Brasília (antes `format(x, "yyyy-MM-dd")` truncava no fuso do processo).
        const salesChart = agruparPedidosPorDia({ todos: allOrdersInRange, pagos: paidOrdersInRange }, FUSO_PADRAO);

        // Ranking placeholder (Mocked as we don't have regions in Order yet, but required by UI)
        const regionRanking = [
            { region: 'Brasil', avgTicket: totalRevenue / (ordersInRange || 1), cancelRate: 0.12 },
            { region: 'México', avgTicket: 0, cancelRate: 0 },
            { region: 'Espanha', avgTicket: 0, cancelRate: 0 }
        ];

        return {
            success: true,
            data: {
                totalStores,
                ordersInRange,
                totalOrdersInRange,
                integrationHealth,
                totalRevenue,
                criticalOrdersCount,
                salesChart,
                ranking: {
                    topTicket: regionRanking[0],
                    bestCancel: regionRanking[0]
                },
                saas: {
                    mrr: billingMetrics.mrr,
                    arr: billingMetrics.arr,
                    totalCash: billingMetrics.totalCash
                }
            }
        };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function getStoreRankingsAction(from?: string, to?: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');

        const now = new Date();
        const startDate = from ? new Date(from) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 30, 0, 0, 0, 0));
        const endDate = to ? new Date(to) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));

        const paidFilter = {
            OR: [
                { paidAt: { gte: startDate, lte: endDate } },
                {
                    status: { in: ['PAID', 'IN_TRANSIT', 'COMPLETED'] } as any,
                    paidAt: null,
                    createdAt: { gte: startDate, lte: endDate }
                }
            ]
        };

        const storeStats = await prisma.store.findMany({
            where: { isDemo: false, isPlatform: false }, // ranking não inclui lojas demo nem a loja-plataforma
            select: {
                id: true,
                name: true,
                slug: true,
                _count: {
                    select: {
                        orders: {
                            where: { createdAt: { gte: startDate, lte: endDate } }
                        }
                    }
                },
                orders: {
                    where: paidFilter,
                    select: { totalBRL: true }
                }
            }
        });

        const computed = storeStats.map((store: any) => {
            const totalOrders = store._count.orders;
            const paidOrders = store.orders.length;
            const revenue = store.orders.reduce((acc: number, o: { totalBRL: any }) => acc + Number(o.totalBRL || 0), 0);
            return {
                storeId: store.id,
                name: store.name,
                slug: store.slug,
                orders: totalOrders,
                paidOrders,
                revenue,
                conversionRate: totalOrders > 0 ? (paidOrders / totalOrders) * 100 : 0,
            };
        });

        const topRevenue = [...computed]
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 5);

        const topVolume = [...computed]
            .sort((a, b) => b.orders - a.orders)
            .slice(0, 5);

        const topConversion = [...computed]
            .filter(s => s.orders >= 5)
            .sort((a, b) => b.conversionRate - a.conversionRate)
            .slice(0, 5);

        return {
            success: true,
            data: { topRevenue, topVolume, topConversion }
        };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}


export async function getEnhancedMetricsAction(from?: string, to?: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');

        const now = new Date();
        const startDate = from ? new Date(from) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 30, 0, 0, 0, 0));
        const endDate = to ? new Date(to) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));

        const paidFilter = {
            store: { isDemo: false },
            OR: [
                { paidAt: { gte: startDate, lte: endDate } },
                {
                    status: { in: ['PAID', 'IN_TRANSIT', 'COMPLETED'] } as any,
                    paidAt: null,
                    createdAt: { gte: startDate, lte: endDate }
                }
            ]
        };

        const last7DaysStart = subDays(new Date(), 7);
        last7DaysStart.setHours(0, 0, 0, 0);

        const [
            ordersInRange,
            totalOrdersInRange,
            totalRevenueAgg,
            activeStores,
            criticalOrders,
            allOrdersInRange,
            paidOrdersInRange,
            storesWithOrdersInPeriod,
            storesWithOrdersLast7Days,
        ] = await Promise.all([
            prisma.order.count({ where: paidFilter }),
            prisma.order.count({ where: { store: { isDemo: false }, createdAt: { gte: startDate, lte: endDate } } }),
            prisma.order.aggregate({
                where: paidFilter,
                _sum: { totalBRL: true }
            }),
            prisma.subscription.count({ where: { status: 'ACTIVE', store: { isDemo: false } } }),
            prisma.order.count({
                where: {
                    status: 'PENDING' as any,
                    createdAt: { gte: startDate, lte: endDate }
                }
            }),
            prisma.order.findMany({
                where: { createdAt: { gte: startDate, lte: endDate } },
                select: { createdAt: true }
            }),
            prisma.order.findMany({
                where: paidFilter,
                select: { totalBRL: true, createdAt: true, paidAt: true }
            }),
            // Distinct stores with ≥1 order in the selected period
            prisma.order.findMany({
                where: { createdAt: { gte: startDate, lte: endDate } },
                select: { storeId: true },
                distinct: ['storeId'],
            }),
            // Distinct stores with ≥1 order in the last 7 days (always hardcoded — by design)
            prisma.order.findMany({
                where: { createdAt: { gte: last7DaysStart } },
                select: { storeId: true },
                distinct: ['storeId'],
            }),
        ]);

        // retentionRate: lojas com pedido no período / total assinaturas ativas × 100
        const retentionRate = activeStores > 0
            ? Math.round((storesWithOrdersInPeriod.length / activeStores) * 100)
            : 0;

        const storesActiveLast7Days = storesWithOrdersLast7Days.length;


        const chartData = agruparPedidosPorDia({ todos: allOrdersInRange, pagos: paidOrdersInRange }, FUSO_PADRAO);

        return {
            success: true,
            data: {
                ordersInRange,
                totalOrdersInRange,
                totalRevenue: Number(totalRevenueAgg._sum.totalBRL || 0),
                activeStores,
                criticalOrders,
                chartData,
                retentionRate,
                storesActiveLast7Days,
            }
        };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function getConversionFunnelAction(from?: string, to?: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');

        const now = new Date();
        const startDate = from ? new Date(from) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 30, 0, 0, 0, 0));
        const endDate = to ? new Date(to) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));

        const ordersByStatus = await prisma.order.groupBy({
            by: ['status'],
            where: {
                createdAt: { gte: startDate, lte: endDate }
            },
            _count: {
                _all: true
            }
        });

        const total = ordersByStatus.reduce((acc: number, curr: any) => acc + curr._count._all, 0);
        const pending = ordersByStatus.find((o: any) => o.status === 'PENDING')?._count._all || 0;
        const paid = ordersByStatus.find((o: any) => o.status === 'PAID')?._count._all || 0;
        const delivered = ordersByStatus.find((o: any) => o.status === 'DELIVERED')?._count._all || 0;
        const canceled = ordersByStatus.find((o: any) => o.status === 'CANCELED')?._count._all || 0;

        return {
            success: true,
            data: {
                total,
                pending,
                paid,
                delivered,
                canceled
            }
        };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function getActivityHeatmapAction(from?: string, to?: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');

        const now = new Date();
        const startDate = from ? new Date(from) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 30, 0, 0, 0, 0));
        const endDate = to ? new Date(to) : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));

        const orders = await prisma.order.findMany({
            where: {
                createdAt: { gte: startDate, lte: endDate }
            },
            select: {
                createdAt: true
            }
        });

        const heatmapData: number[][] = Array(7).fill(0).map(() => Array(24).fill(0));

        orders.forEach((order: { createdAt: Date }) => {
            const date = new Date(order.createdAt);
            const dayOfWeek = date.getDay(); // 0 = Sunday
            const hour = date.getHours();
            heatmapData[dayOfWeek][hour]++;
        });

        return {
            success: true,
            data: heatmapData
        };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function updateStoreTrialAction(storeId: string, newTrialEndsAt: Date): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.manage');

        const subscription = await prisma.subscription.findUnique({
            where: { storeId },
            select: { trialEndsAt: true, status: true }
        });

        if (!subscription) {
            return { success: false, error: "Assinatura não encontrada para esta loja." };
        }

        const oldDate = subscription.trialEndsAt;

        // Update trial date and FORCE status to TRIALING
        await prisma.subscription.update({
            where: { storeId },
            data: {
                trialEndsAt: newTrialEndsAt,
                status: "TRIALING",
                // If it was canceled or expired, we might want to ensure currentPeriodEnd is also moved?
                // But the requirement specifically says "trialEndsAt" and "status: TRIALING".
            }
        });

        // Audit Log
        const { AuditService } = await import("@/services/audit.service");

        await AuditService.log({
            userId: session.user.id,
            userEmail: session.user.email ?? undefined,
            action: "UPDATE_TRIAL_DAYS",
            resource: "STORE",
            resourceId: storeId,
            details: {
                // Auditoria é de plataforma: Brasília explícito (antes, o fuso do processo).
                dataAntiga: oldDate ? formatarInstante(oldDate, FUSO_PADRAO, "dataHoraEspaco") : "Nenhuma",
                novaDataExpiração: formatarInstante(newTrialEndsAt, FUSO_PADRAO, "dataHoraEspaco"),
                novoStatus: "EM_TRIAL",
                observação: "Ajuste manual pelo Super Admin"
            },
            storeId: storeId
        });

        revalidatePath("/admin/super/stores");
        revalidatePath(`/admin/super/stores/${storeId}`); // If relevant

        return { success: true };
    } catch (error: any) {
        console.error("Error updating trial days:", error);
        return { success: false, error: error.message };
    }
}

// --- User Actions ---

export async function createUserAction(data: CreateUserDTO): Promise<ActionResponse> {
    try {
        // Sempre cria role global USER (linha abaixo) — sem vetor de escalação a ADMIN.
        const session = await getAdminSession('platform.users.manage');

        // 1. Check if email exists (case-insensitive)
        const existingUser = await prisma.user.findFirst({
            where: { email: { equals: data.email.toLowerCase().trim(), mode: 'insensitive' } }
        });
        if (existingUser) {
            return { success: false, error: "E-mail já cadastrado." };
        }

        // 2. Verify Store exists
        const store = await prisma.store.findUnique({ where: { id: data.storeId } });
        if (!store) {
            return { success: false, error: "Loja não encontrada." };
        }

        // 3. Resolve/Create Role
        let roleSlug = "";
        let roleName = "";

        if (data.roleType === "STORE_OWNER") {
            roleSlug = "admin";
            roleName = "Administrador";
        } else {
            roleSlug = "staff";
            roleName = "Staff";
        }

        // Ensure Role exists in that store
        // We use Upsert to ensure consistency across stores
        const role = await prisma.role.upsert({
            where: {
                storeId_slug: {
                    storeId: store.id,
                    slug: roleSlug
                }
            },
            update: {}, // Don't change existing
            create: {
                name: roleName,
                slug: roleSlug,
                storeId: store.id,
                description: `Cargo gerado automaticamente`
            }
        });

        // Se for um cargo novo de admin/owner, garante que tenha todas as permissões
        if (roleSlug === 'admin' || roleSlug === 'owner') {
            const currentPermsCount = await prisma.rolePermission.count({
                where: { roleId: role.id }
            });

            if (currentPermsCount === 0) {
                // Só permissões de LOJA no cargo da loja — antes "todas", inclusive `platform.*` ([R120]).
                const allPermissions = permissoesParaCargo(role, await prisma.permission.findMany());
                await prisma.rolePermission.createMany({
                    data: allPermissions.map((p: { id: string }) => ({
                        roleId: role.id,
                        permissionId: p.id
                    }))
                });
            }
        }

        // 4. Create User
        const normalizedEmail = data.email.toLowerCase().trim();
        const hashedPassword = await bcrypt.hash(data.password, 12);

        const newUser = await prisma.user.create({
            data: {
                name: data.name,
                email: normalizedEmail,
                password: hashedPassword,
                role: UserRole.USER, // Regular user global role, but elevated via store/roles
            },
            // `select` explícito: sem ele o Prisma devolve `password` (hash), `twoFactorSecret`,
            // `twoFactorBackupCodes` e `passwordResetToken` — e toda coluna sensível NOVA do
            // schema passaria a vir junto, em silêncio.
            select: { id: true }
        });

        // 5. Link to Store
        await prisma.userStore.create({
            data: {
                userId: newUser.id,
                storeId: store.id
            }
        });

        // 6. Assign Role
        await prisma.userRoleAssign.create({
            data: {
                userId: newUser.id,
                roleId: role.id
            }
        });

        // 7. Sync to Chatwoot if enabled
        if (store.chatwootAccountId) {
            try {
                // Se for OWNER, garante privilégios de ADMINISTRATOR no Chatwoot
                const isAdmin = data.roleType === "STORE_OWNER";

                await ChatwootPlatformService.provisionUser(
                    store.id,
                    store.chatwootAccountId!,
                    data.email,
                    data.name,
                    isAdmin ? "administrator" : "agent"
                );
                console.log(`[CHATWOOT] Usuário ${data.email} sincronizado na conta ${store.chatwootAccountId} (Admin: ${isAdmin})`);
            } catch (cwError) {
                console.error("[CHATWOOT ERROR] Falha ao sincronizar usuário no Chatwoot:", cwError);
            }
        }

        // 8. Audit Log
        await prisma.auditLog.create({
            data: {
                action: "USER_CREATE",
                resource: "User",
                resourceId: newUser.id,
                details: { storeId: store.id, role: roleSlug, createdBy: session.user.email },
                storeId: store.id, // Log associated with the store
                userId: session.user.id,
                userEmail: session.user.email
            }
        });

        revalidatePath("/admin/super/users");
        // 🔴 NÃO devolver o registro do Prisma. `ActionResponse.data` é serializado e enviado
        // ao browser (o consumidor é `"use client"`), e a linha crua carrega colunas que nunca
        // podem cruzar essa fronteira. Os consumidores usam só `success`/`error` — conferido
        // arquivo a arquivo antes de estreitar. Travado por
        // `src/lib/__tests__/linha-crua-no-cliente.test.ts`.
        return { success: true, data: { id: newUser.id } };

    } catch (error: any) {
        console.error("Erro ao criar usuário:", error);
        return { success: false, error: error.message };
    }
}

export async function listUsersAction(params?: { page?: number; pageSize?: number; search?: string; isActive?: boolean }): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.users.view');

        const pageSize = params?.pageSize ?? 15;
        const page = Math.max(1, params?.page ?? 1);
        const skip = (page - 1) * pageSize;

        const where: PrismaClient.Prisma.UserWhereInput = {};
        if (params?.search?.trim()) {
            const term = params.search.trim();
            where.OR = [
                { name: { contains: term, mode: 'insensitive' } },
                { email: { contains: term, mode: 'insensitive' } },
            ];
        }
        if (params?.isActive !== undefined) {
            where.isActive = params.isActive;
        }

        const [users, totalCount] = await Promise.all([
            prisma.user.findMany({
                where,
                orderBy: { createdAt: 'desc' },
                take: pageSize,
                skip,
                include: {
                    userStores: {
                        include: { store: true }
                    },
                    userRoles: {
                        include: { role: true }
                    }
                }
            }),
            prisma.user.count({ where })
        ]);

        const totalPages = Math.ceil(totalCount / pageSize);

        // Simplify payload for frontend
        const safeUsers = users.map((u: any) => ({
            id: u.id,
            name: u.name,
            email: u.email,
            globalRole: u.role,
            isActive: u.isActive,
            sessionVersion: u.sessionVersion,
            isHiddenFromReports: u.isHiddenFromReports,
            stores: u.userStores.map((us: any) => us.store.name).join(", "),
            storeIds: u.userStores.map((us: any) => us.store.id),
            roles: u.userRoles.map((ur: any) => ur.role.name).join(", "),
            createdAt: u.createdAt,
            oauthProvider: u.oauthProvider
        }));

        return { success: true, data: { users: safeUsers, totalCount, page, totalPages } };

    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function updateUserAction(data: UpdateUserDTO): Promise<ActionResponse> {
    try {
        const adminSession = await getAdminSession('platform.users.manage');

        const target = await prisma.user.findUnique({
            where: { id: data.id },
            select: { id: true, role: true },
        });
        const guard = canMutatePlatformUser(
            { id: adminSession.user.id, isFullAdmin: isFullAdmin(adminSession) },
            target,
            { nextRole: data.role },
        );
        if (!guard.allowed) return { success: false, error: guard.reason };

        const updateData: any = {
            name: data.name,
            email: data.email,
            role: data.role,
            isHiddenFromReports: data.isHiddenFromReports,
        };

        if (data.password && data.password.trim() !== "") {
            updateData.password = await bcrypt.hash(data.password, 12);
            updateData.sessionVersion = { increment: 1 }; // Force logout on password change
        }

        await prisma.user.update({
            where: { id: data.id },
            data: updateData
        });

        await prisma.auditLog.create({
            data: {
                action: "USER_UPDATE",
                resource: "User",
                resourceId: data.id,
                details: { updatedFields: Object.keys(updateData), adminEmail: adminSession.user.email },
                userId: adminSession.user.id,
                userEmail: adminSession.user.email
            }
        });

        revalidatePath("/admin/super/users", "page");
        revalidateTag('all-reports', "default");
        // 🔴 NÃO devolver o registro do Prisma. `ActionResponse.data` é serializado e enviado
        // ao browser (o consumidor é `"use client"`), e a linha crua carrega colunas que nunca
        // podem cruzar essa fronteira. Os consumidores usam só `success`/`error` — conferido
        // arquivo a arquivo antes de estreitar. Travado por
        // `src/lib/__tests__/linha-crua-no-cliente.test.ts`.
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function toggleUserStatusAction(userId: string): Promise<ActionResponse> {
    try {
        const adminSession = await getAdminSession('platform.users.manage');

        const user = await prisma.user.findUnique({ where: { id: userId } });
        if (!user) return { success: false, error: "Usuário não encontrado." };

        const guard = canMutatePlatformUser(
            { id: adminSession.user.id, isFullAdmin: isFullAdmin(adminSession) },
            { id: user.id, role: user.role },
        );
        if (!guard.allowed) return { success: false, error: guard.reason };

        await prisma.user.update({
            where: { id: userId },
            data: {
                isActive: !user.isActive,
                sessionVersion: !user.isActive ? user.sessionVersion : { increment: 1 } // Increment if blocking
            }
        });

        await prisma.auditLog.create({
            data: {
                action: user.isActive ? "USER_BLOCK" : "USER_UNBLOCK",
                resource: "User",
                resourceId: userId,
                details: { adminEmail: adminSession.user.email },
                userId: adminSession.user.id,
                userEmail: adminSession.user.email
            }
        });

        revalidatePath("/admin/super/users");
        // 🔴 NÃO devolver o registro do Prisma. `ActionResponse.data` é serializado e enviado
        // ao browser (o consumidor é `"use client"`), e a linha crua carrega colunas que nunca
        // podem cruzar essa fronteira. Os consumidores usam só `success`/`error` — conferido
        // arquivo a arquivo antes de estreitar. Travado por
        // `src/lib/__tests__/linha-crua-no-cliente.test.ts`.
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function inactivateUserAction(userId: string): Promise<ActionResponse> {
    try {
        const adminSession = await getAdminSession('platform.users.manage');

        const user = await prisma.user.findUnique({ where: { id: userId } });
        if (!user) return { success: false, error: "Usuário não encontrado." };

        const guard = canMutatePlatformUser(
            { id: adminSession.user.id, isFullAdmin: isFullAdmin(adminSession) },
            { id: user.id, role: user.role },
        );
        if (!guard.allowed) return { success: false, error: guard.reason };

        await prisma.user.update({
            where: { id: userId },
            data: {
                isActive: false,
                sessionVersion: { increment: 1 }
            }
        });

        await prisma.auditLog.create({
            data: {
                action: "USER_INACTIVATE",
                resource: "User",
                resourceId: userId,
                details: { adminEmail: adminSession.user.email, targetEmail: user.email },
                userId: adminSession.user.id,
                userEmail: adminSession.user.email
            }
        });

        revalidatePath("/admin/super/users");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function forceLogoutAllAction(): Promise<ActionResponse> {
    try {
        const adminSession = await getAdminSession();

        const { count } = await prisma.user.updateMany({
            data: { sessionVersion: { increment: 1 } }
        });

        await prisma.auditLog.create({
            data: {
                action: "FORCE_LOGOUT_ALL",
                resource: "User",
                details: { affectedUsers: count, adminEmail: adminSession.user.email },
                userId: adminSession.user.id,
                userEmail: adminSession.user.email
            }
        });

        revalidatePath("/admin/super/users");
        return { success: true, data: { count } };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function forceUserLogoutAction(userId?: string, email?: string): Promise<ActionResponse> {
    try {
        const adminSession = await getAdminSession('platform.users.manage');

        let target: { id: string; role: string } | null = null;

        if (!userId && !email) {
            return { success: false, error: "É necessário fornecer o ID ou o E-mail do usuário." };
        }

        if (userId) {
            target = await prisma.user.findUnique({
                where: { id: userId },
                select: { id: true, role: true }
            });
            if (!target) return { success: false, error: "Usuário não encontrado." };
        } else {
            target = await prisma.user.findUnique({
                where: { email: email! },
                select: { id: true, role: true }
            });
            if (!target) return { success: false, error: "Usuário não encontrado com este e-mail." };
        }

        const guard = canMutatePlatformUser(
            { id: adminSession.user.id, isFullAdmin: isFullAdmin(adminSession) },
            target,
        );
        if (!guard.allowed) return { success: false, error: guard.reason };

        const targetId = target.id;

        await prisma.user.update({
            where: { id: targetId },
            data: { sessionVersion: { increment: 1 } }
        });

        await prisma.auditLog.create({
            data: {
                action: "USER_FORCE_LOGOUT",
                resource: "User",
                resourceId: targetId,
                details: {
                    adminEmail: adminSession.user.email,
                    method: userId ? "by_id" : "by_email",
                    providedEmail: email
                },
                userId: adminSession.user.id,
                userEmail: adminSession.user.email
            }
        });

        revalidatePath("/admin/super/users");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function updateUserStoresAction(userId: string, storeIds: string[]): Promise<ActionResponse> {
    try {
        const adminSession = await getAdminSession('platform.users.manage');

        const target = await prisma.user.findUnique({
            where: { id: userId },
            select: { id: true, role: true },
        });
        // Trava: staff não vincula lojas a ADMINs nem a si mesmo (auto-acesso cross-tenant).
        const guard = canMutatePlatformUser(
            { id: adminSession.user.id, isFullAdmin: isFullAdmin(adminSession) },
            target,
        );
        if (!guard.allowed) return { success: false, error: guard.reason };

        // 1. Buscar lojas atuais antes da mudança para comparar
        const currentStores = await prisma.userStore.findMany({
            where: { userId },
            include: { store: { select: { id: true, chatwootAccountId: true } } }
        });

        // Transaction to sync user stores
        await prisma.$transaction([
            // 2. Remove all current stores
            prisma.userStore.deleteMany({ where: { userId } }),
            // 3. Add new ones
            prisma.userStore.createMany({
                data: storeIds.map(storeId => ({
                    userId,
                    storeId
                }))
            }),
            // 3b. A sessão carrega a loja ATIVA e os cargos dela ([R51]); vínculo removido tem de
            // derrubar o JWT vivo, senão o usuário segue operando a loja de que foi desvinculado.
            prisma.user.update({ where: { id: userId }, data: { sessionVersion: { increment: 1 } } }),
        ]);

        // 4. Sincronização com Chatwoot (Lógica Delta)
        const user = await prisma.user.findUnique({
            where: { id: userId },
            select: { email: true, name: true }
        });

        if (user) {
            const oldChatwootAccounts = currentStores
                .map((s: any) => s.store.chatwootAccountId)
                .filter((id: any) => !!id) as number[];

            const newStores = await prisma.store.findMany({
                where: { id: { in: storeIds } },
                select: { id: true, chatwootAccountId: true }
            });
            const newChatwootAccounts = newStores
                .map((s: any) => s.chatwootAccountId)
                .filter((id: any) => !!id) as number[];

            // Contas para remover: Estavam nas antigas mas não estão nas novas
            const toRemove = oldChatwootAccounts.filter(id => !newChatwootAccounts.includes(id));
            // Contas para adicionar: Estão nas novas mas não estavam nas antigas
            const toAdd = newChatwootAccounts.filter(id => !oldChatwootAccounts.includes(id));

            for (const accountId of toRemove) {
                try {
                    const store = currentStores.find((cs: any) => cs.store.chatwootAccountId === accountId);
                    if (store) {
                        await ChatwootPlatformService.unlinkUserByEmail(store.store.id, accountId, user.email);
                    }
                } catch (e) {
                    console.error(`[CHATWOOT SYNC] Failed to remove user from account ${accountId} `, e);
                }
            }

            for (const accountId of toAdd) {
                try {
                    // Buscar a role que foi atribuída (pode ser complexo aqui, mas vamos usar a global por agora)
                    const globalRole = await prisma.userRoleAssign.findFirst({
                        where: { userId },
                        include: { role: true }
                    });
                    const roleSlug = globalRole?.role.slug || "user";

                    const storeMatch = newStores.find((s: any) => s.chatwootAccountId === accountId);

                    if (storeMatch) {
                        await ChatwootPlatformService.provisionUser(
                            storeMatch.id,
                            accountId,
                            user.email,
                            user.name || "Membro",
                            (roleSlug === 'owner' || roleSlug === 'admin') ? "administrator" : "agent"
                        );
                    }
                } catch (e) {
                    console.error(`[CHATWOOT SYNC] Failed to add user to account ${accountId} `, e);
                }
            }
        }

        await prisma.auditLog.create({
            data: {
                action: "USER_STORES_UPDATE",
                resource: "User",
                resourceId: userId,
                details: { storeIds, adminEmail: adminSession.user.email },
                userId: adminSession.user.id,
                userEmail: adminSession.user.email
            }
        });

        revalidatePath("/admin/super/users");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

// --- Impersonation Actions ---

export async function impersonateStoreWithPinAction(storeId: string, pin: string): Promise<ActionResponse> {
    try {
        const session = await getAdminSession();

        if (!(await checkRateLimit(`impersonate:${storeId}`, 3, 900_000))) {
            return { success: false, error: "Muitas tentativas. Aguarde 15 minutos." };
        }

        const store = await prisma.store.findUnique({ where: { id: storeId } });
        if (!store) {
            return { success: false, error: "Loja não encontrada." };
        }

        // Validate PIN
        const supportCode = await prisma.storeSupportCode.findFirst({
            where: {
                storeId,
                code: pin,
                isUsed: false,
                expiresAt: { gt: new Date() }
            }
        });

        if (!supportCode) {
            return { success: false, error: "Código PIN inválido ou expirado." };
        }

        // Mark as used
        await prisma.storeSupportCode.update({
            where: { id: supportCode.id },
            data: { isUsed: true }
        });

        // Set secure cookies
        const cookieStore = await cookies();
        cookieStore.set("impersonation_store_id", storeId, {
            secure: process.env.NODE_ENV === "production",
            httpOnly: true,
            path: "/",
            sameSite: "lax"
        });

        // Additional flags for traceability
        cookieStore.set("is_impersonating", "true", {
            secure: process.env.NODE_ENV === "production",
            httpOnly: false, // Accessible to JS for the banner
            path: "/",
            sameSite: "lax"
        });
        cookieStore.set("impersonator_admin_id", session.user.id, {
            secure: process.env.NODE_ENV === "production",
            httpOnly: true,
            path: "/",
            sameSite: "lax"
        });

        await prisma.auditLog.create({
            data: {
                action: "IMPERSONATION_START",
                resource: "Store",
                resourceId: storeId,
                details: {
                    description: `Super Admin iniciou impersonation na loja ID: ${storeId} com código PIN`,
                    adminEmail: session.user.email
                },
                userId: session.user.id,
                userEmail: session.user.email ?? undefined,
                storeId: storeId
            }
        });

        return { success: true };

    } catch (error: any) {
        console.error("Erro ao iniciar impersonation com PIN:", error);
        return { success: false, error: error.message };
    }
}

export async function stopImpersonationAction(): Promise<void> {
    const session = await auth();

    const cookieStore = await cookies();
    cookieStore.delete("impersonation_store_id");
    cookieStore.delete("is_impersonating");
    cookieStore.delete("impersonator_admin_id");

    if (session?.user) {
        await prisma.auditLog.create({
            data: {
                action: "IMPERSONATION_END",
                resource: "Store",
                details: { description: "Impersonation finalizado" },
                userId: session.user.id,
                userEmail: session.user.email
            }
        });
    }

    redirect("/admin/super/stores");
}

export async function getWebhookHealthMetricsAction(): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.observability.view');

        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

        const logs = await prisma.auditLog.findMany({
            where: {
                action: { startsWith: "WEBHOOK_" },
                createdAt: { gte: oneHourAgo }
            },
            orderBy: { createdAt: 'desc' },
            include: { store: { select: { name: true, slug: true } } }
        });

        // Single-pass grouping: key = storeId ?? "unknown"
        type StoreEntry = {
            name: string;
            slug: string;
            total: number;
            success: number;
            latencies: number[];
            consecutiveFailsActive: boolean; // still counting streak from most-recent
            consecutiveFails: number;
        };

        const storeMap = new Map<string, StoreEntry>();

        for (const log of logs) {
            const key = log.storeId ?? "unknown";
            const isUnknown = key === "unknown";
            const storeName = log.store?.name ?? (isUnknown ? "Desconhecido" : "Loja Removida");
            const storeSlug = log.store?.slug ?? (isUnknown ? "?" : "removed");

            if (!storeMap.has(key)) {
                storeMap.set(key, {
                    name: storeName,
                    slug: storeSlug,
                    total: 0,
                    success: 0,
                    latencies: [],
                    consecutiveFailsActive: true,
                    consecutiveFails: 0,
                });
            }

            const entry = storeMap.get(key)!;
            entry.total++;

            // Success: RECEIVED, SUCCESS, or SYNC (Yampi successful processing)
            const isSuccess =
                log.action.includes("SUCCESS") ||
                log.action.includes("RECEIVED") ||
                log.action.includes("SYNC");

            // Failure: FAIL, ERROR, or UNAUTHORIZED
            const isFail =
                log.action.includes("FAIL") ||
                log.action.includes("ERROR") ||
                log.action.includes("UNAUTHORIZED");

            if (isSuccess) entry.success++;

            // Consecutive fail streak: logs are DESC (newest first).
            // Count only while we haven't seen a non-fail yet.
            if (entry.consecutiveFailsActive) {
                if (isFail) {
                    entry.consecutiveFails++;
                } else {
                    entry.consecutiveFailsActive = false;
                }
            }

            // Latency: support both `latency` (Shopify) and `latency_ms` (Yampi legacy)
            const details = log.details as Record<string, unknown> | null;
            const latency = details?.latency ?? details?.latency_ms;
            if (typeof latency === 'number') {
                entry.latencies.push(latency);
            }
        }

        const metrics = Array.from(storeMap.entries()).map(([id, entry]) => {
            const avgLatency =
                entry.latencies.length > 0
                    ? Math.round(entry.latencies.reduce((a, b) => a + b, 0) / entry.latencies.length)
                    : 0;

            const successRate =
                entry.total > 0 ? Math.round((entry.success / entry.total) * 100) : 0;

            return {
                storeId: id,
                name: entry.name,
                slug: entry.slug,
                totalRequests: entry.total,
                successRate,
                avgLatency,
                isAlert: entry.consecutiveFails >= 5,
            };
        });

        // O balde `unknown` agrupa TODA linha `WEBHOOK_*` sem loja. Ele continua visível na
        // Análise Setorial (com o badge por linha vindo de `isAlert`), mas **não** dispara o
        // "Protocolo de Emergência" da plataforma inteira: um alarme global sobre uma loja que o
        // super admin não consegue identificar nem acionar é ruído que treina a ignorar o banner.
        // A rota nua do Shopify passou a gravar com `storeId: null` de propósito — o HMAC prova
        // que a Shopify enviou, não de qual loja —, então é ela que alimenta este balde.
        // A observabilidade dela vive em ErrorEvent (`WEBHOOK_SIGNATURE_INVALID`), não aqui.
        const globalAlert = metrics.some(r => r.isAlert && r.storeId !== "unknown");

        return { success: true, data: { metrics, globalAlert } };

    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function getSystemMetricsAction(): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.observability.view');
        const metrics = await SystemMonitorService.getMetrics();

        return {
            success: true,
            data: metrics
        };
    } catch (error: any) {
        console.error("Erro na action getSystemMetricsAction:", error);
        return { success: false, error: error.message };
    }
}


export async function listStoreUsersAction(storeId: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');

        return await withTenant(storeId, async () => {
            const users = await UserService.listStoreUsers(storeId);
            const mapped = users.map((u: any) => ({
                id: u.id,
                name: u.name,
                email: u.email,
                role: u.roles[0]?.slug || "user",
                lastActiveAt: u.lastActiveAt?.toISOString?.() ?? null
            }));
            return { success: true, data: mapped };
        });
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function getOnlineStoreUsersAction(
    storeId: string
): Promise<ActionResponse<{ userId: string; status: 'active' | 'idle' }[]>> {
    try {
        await getAdminSession('platform.stores.view');

        const nowUnix = Math.floor(Date.now() / 1000);
        const cutoff = nowUnix - 120; // 2 minutes TTL mirror

        // 1. Limpar zumbis e buscar IDs online
        const pipe1 = redis.pipeline();
        pipe1.zremrangebyscore(`online:${storeId}`, "-inf", cutoff);
        pipe1.zrange(`online:${storeId}`, 0, -1);
        const results1 = await pipe1.exec();
        
        const zrangeResult = results1?.[1]?.[1] as string[] | null;
        const userIds = zrangeResult ?? [];
        
        if (userIds.length === 0) return { success: true, data: [] };

        // 2. Prevenção de N+1: pipeline único para buscar presence de todos
        const pipe2 = redis.pipeline();
        userIds.forEach((uid) => pipe2.get(`presence:${uid}`));
        const presenceResults = await pipe2.exec();

        // 3. Fail-safe de TypeError no parse
        const data = userIds.map((userId, idx) => {
            const raw = presenceResults?.[idx]?.[1] as string | null;
            const parts = raw ? raw.split('|') : [];
            const status = parts[2] === 'idle' ? 'idle' as const : 'active' as const;
            return { userId, status };
        });

        return { success: true, data };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function updateUserStoreRoleAction(userId: string, storeId: string, roleSlug: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        // 1. Garantir que a Role existe para esta loja
        let role = await prisma.role.findFirst({
            where: { storeId, slug: roleSlug }
        });

        if (!role) {
            // Se não existe, cria (Básico)
            role = await prisma.role.create({
                data: {
                    name: roleSlug.toUpperCase(),
                    slug: roleSlug,
                    storeId: storeId
                }
            });
        }

        // 2. Remover roles atuais deste usuário NESTA LOJA
        const currentRoles = await prisma.userRoleAssign.findMany({
            where: {
                userId,
                role: { storeId }
            }
        });

        if (currentRoles.length > 0) {
            await prisma.userRoleAssign.deleteMany({
                where: {
                    userId,
                    roleId: { in: currentRoles.map(cr => cr.roleId) }
                }
            });
        }

        // 3. Sincronizar Permissões da Role
        const OWNER_PERMS = [
            'dashboard.view', 'orders.view', 'orders.manage', 'customers.view', 'customers.manage',
            'products.view', 'products.manage', 'carriers.view', 'carriers.manage',
            'integrations.view', 'integrations.manage', 'partners.view', 'partners.manage',
            'financial.view', 'settings.view', 'settings.manage',
            'store-users.view', 'store-users.manage', 'store-roles.view', 'store-roles.manage', 'audit.view'
        ];

        const USER_PERMS = [
            'dashboard.view', 'orders.view', 'customers.view', 'carriers.view', 'products.view'
        ];

        const targetPerms = (roleSlug === 'owner' || roleSlug === 'admin') ? OWNER_PERMS : USER_PERMS;

        // Buscar IDs das permissões
        const dbPerms = await prisma.permission.findMany({
            where: { slug: { in: targetPerms } }
        });

        // Limpar permissões atuais da role e reinserir (Sync)
        await prisma.rolePermission.deleteMany({
            where: { roleId: role.id }
        });

        // [escopo: lista-fixa] OWNER_PERMS/USER_PERMS são literais de loja conferidos acima — sem
        // `platform.*` nem legado. Trava: escopo-de-permissao-escritores.test.ts
        await prisma.rolePermission.createMany({
            data: dbPerms.map(p => ({
                roleId: role.id,
                permissionId: p.id
            }))
        });

        // 4. Atribuir nova Role ao usuário
        await prisma.userRoleAssign.create({
            data: {
                userId,
                roleId: role.id
            }
        });

        // 5. Provisionamento Automático no Chatwoot (se houver conta vinculada)
        const userStore = await prisma.store.findUnique({
            where: { id: storeId },
            select: { chatwootAccountId: true, name: true }
        });

        if (userStore?.chatwootAccountId) {
            const user = await prisma.user.findUnique({
                where: { id: userId },
                select: { email: true, name: true }
            });

            if (user) {
                console.log(`[CHATWOOT AUTO - PROVISION] Provisioning ${user.email} for account ${userStore.chatwootAccountId}`);
                try {
                    // O cargo no Chatwoot é identificado pelo `Role.name`, que é o mesmo que
                    // o SSO envia (`chatwoot-sso-guard.ts`). Antes daqui saía
                    // `roleSlug.toUpperCase()` — uma SEGUNDA convenção: trocar o cargo criava
                    // um custom role `ADMIN` e abrir o Atendimento criava outro,
                    // `Administrador`. Dois cargos por conta, com o usuário oscilando entre
                    // eles conforme o gatilho.
                    await ChatwootPlatformService.provisionUser(
                        storeId,
                        userStore.chatwootAccountId,
                        user.email,
                        user.name || "Membro",
                        role.name,
                        undefined,
                        role.slug,
                    );
                } catch (cwError) {
                    console.error("[CHATWOOT AUTO-PROVISION ERROR]", cwError);
                    // Não travamos a action se o Chatwoot falhar, apenas logamos
                }
            }
        }

        // 6. Incrementar sessionVersion para forçar atualização do JWT (Logout Industrial)
        await prisma.user.update({
            where: { id: userId },
            data: { sessionVersion: { increment: 1 } }
        });

        revalidatePath("/admin/super/stores/[slug]", "page");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function listSubscriptionPlansAction(): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.plans.view');
        const plans = await prisma.plan.findMany({
            include: { limits: true },
            orderBy: { createdAt: "asc" },
        });

        const serializedPlans = plans.map((plan: any) => ({
            ...plan,
            basePrice: Number(plan.basePrice)
        }));

        return { success: true, data: serializedPlans };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}


export async function updateStorePlanAction(storeId: string, planId: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        const plan = await prisma.plan.findUnique({
            where: { id: planId },
            include: { limits: true }
        });

        if (!plan) throw new Error("Plano não encontrado");

        const store = await prisma.store.findUnique({
            where: { id: storeId },
            select: { id: true, name: true, chatwootAccountId: true }
        });

        if (!store) throw new Error("Loja não encontrada");

        // Automação Chatwoot: Se o novo plano tem Chatwoot
        const needsChatwoot = plan.limits?.hasCustomDomain || false; // Using hasCustomDomain as proxy for now or check limits

        let chatwootAccountId = store.chatwootAccountId;

        if (needsChatwoot) {
            if (!chatwootAccountId) {
                console.log(`[CHATWOOT AUTO-CONFIG] Creating account for store: ${store.name}`);
                try {
                    const cwAccount = await ChatwootPlatformService.createAccount(store.id, store.name);
                    chatwootAccountId = cwAccount.id;
                } catch (cwError) {
                    console.error("[CHATWOOT AUTO-CONFIG ERROR] Failed to create account:", cwError);
                }
            }

            if (chatwootAccountId) {
                const members = await prisma.userStore.findMany({
                    where: { storeId },
                    include: {
                        user: {
                            include: {
                                userRoles: {
                                    where: { role: { storeId } },
                                    include: { role: true }
                                }
                            }
                        }
                    }
                });

                for (const member of members) {
                    const role = member.user.userRoles[0]?.role;
                    const roleSlug = role?.slug || "agent";
                    const isAdmin = roleSlug === 'owner' || roleSlug === 'admin';

                    try {
                        // `role.name`, não `roleSlug.toUpperCase()` — ver o comentário no
                        // outro call site: eram duas convenções para o mesmo cargo.
                        // Membro sem cargo na loja segue como `agent` nativo.
                        await ChatwootPlatformService.provisionUser(
                            storeId,
                            chatwootAccountId,
                            member.user.email,
                            member.user.name || "Membro",
                            role?.name || "agent",
                            undefined,
                            role?.slug,
                        );
                    } catch (pError) {
                        console.error(`[CHATWOOT AUTO-PROVISION ERROR] User ${member.user.email}: `, pError);
                    }
                }
            }
        }

        // ⚡ CRITICAL: Fetch existing subscription to preserve TRIALING status
        const existingSubscription = await prisma.subscription.findUnique({
            where: { storeId },
            select: { status: true, trialEndsAt: true }
        });

        // Preserve TRIALING status if trial is still active, otherwise default to ACTIVE
        const preservedStatus = existingSubscription?.status || "ACTIVE";

        // Atomic update of Subscription and Store
        await prisma.$transaction([
            prisma.subscription.upsert({
                where: { storeId },
                create: {
                    storeId,
                    planId,
                    status: "TRIALING", // New stores start in trial
                    currentPeriodStart: new Date(),
                    currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
                    trialEndsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7-day trial
                },
                update: {
                    planId,
                    // ✅ PRESERVE STATUS: Only update planId, keep status/trial intact
                    // status is NOT updated here - Admin must change status explicitly via updateStoreTrialAction
                }
            }),
            prisma.store.update({
                where: { id: storeId },
                data: {
                    chatwootAccountId: chatwootAccountId
                }
            })
        ]);

        // 🔥 FORCE SESSION REFRESH: Increment sessionVersion for all store users to clear permission cache
        const storeUsers = await prisma.userStore.findMany({
            where: { storeId },
            select: { userId: true }
        });

        if (storeUsers.length > 0) {
            await prisma.user.updateMany({
                where: {
                    id: { in: storeUsers.map(u => u.userId) }
                },
                data: {
                    sessionVersion: { increment: 1 }
                }
            });
            console.log(`[PLAN_UPDATE] Forced session refresh for ${storeUsers.length} users to apply new plan limits immediately`);
        }

        console.log(`[PLAN_UPDATE] Subscription for store ${storeId} updated to ${plan.name} (Status: ${preservedStatus})`);

        revalidatePath("/admin/super/stores/[slug]", "page");
        revalidatePath("/admin/super/stores");
        revalidatePath("/dashboard"); // Force dashboard re-fetch for instant limit updates

        return { success: true };
    } catch (error: any) {
        console.error("Erro ao atualizar plano da loja:", error);
        return { success: false, error: error.message };
    }
}

export async function updateStoreChatwootAction(storeId: string, accountId: number | null): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.support.manage');

        await prisma.store.update({
            where: { id: storeId },
            data: { chatwootAccountId: accountId }
        });

        revalidatePath("/admin/super/stores/[slug]", "page");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

// --- Chatwoot Permission Management Actions ---

export async function listChatwootRoleTemplatesAction(): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.support.view');
        const templates = await prisma.chatwootRoleTemplate.findMany({
            orderBy: { name: "asc" }
        });
        return { success: true, data: templates };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function deleteChatwootRoleTemplateAction(id: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.support.manage');
        await prisma.chatwootRoleTemplate.delete({ where: { id } });
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function getChatwootRoleTemplateAction(name: string = "OWNER"): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.support.view');
        const template = await prisma.chatwootRoleTemplate.findUnique({
            where: { name }
        });
        return { success: true, data: template };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function updateChatwootRoleTemplateAction(name: string, permissions: string[]): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.support.manage');
        const template = await prisma.chatwootRoleTemplate.upsert({
            where: { name },
            update: { permissions },
            create: { name, permissions }
        });

        await prisma.auditLog.create({
            data: {
                action: "CHATWOOT_TEMPLATE_UPDATE",
                resource: "ChatwootRoleTemplate",
                resourceId: template.id,
                details: { name, permissions },
            }
        });

        return { success: true, data: template };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function syncChatwootPermissionsAction(): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.support.manage');
        // Chamada assíncrona para não travar o loader? 
        // Não, em Server Actions é melhor retornar quando terminar ou disparar um worker se for demorar muito.
        // Dado o delay de 300ms, se houver 100 lojas, levará 30 segundos.
        // O Next.js Server Action tem timeout de 60s geralmente (Vercel), na Hostinger (Brasil) depende do Nginx/Node.
        // Vou rodar e retornar.

        await ChatwootPlatformService.syncRolePermissions();

        await prisma.auditLog.create({
            data: {
                action: "CHATWOOT_PERMISSIONS_SYNC",
                resource: "System",
                details: { message: "Sincronização global iniciada via Super Admin" },
            }
        });

        return { success: true };
    } catch (error: any) {
        console.error("Erro na sincronização:", error);
        return { success: false, error: error.message };
    }
}

export async function updateStoreChatwootPermissionsAction(storeId: string, permissions: string[] | Record<string, string[]> | null): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.support.manage');

        await prisma.store.update({
            where: { id: storeId },
            data: { chatwootCustomPermissions: (permissions || null) as any }
        });

        // Se houver conta Chatwoot, já tentar atualizar o cargo lá (Opcional, mas bom UX)
        const store = await prisma.store.findUnique({
            where: { id: storeId },
            select: { id: true, chatwootAccountId: true }
        });

        // A falha aqui PRECISA chegar na tela. Antes ela morria num `console.error`: o painel
        // dizia "salvo", o banco guardava a sobrescrição, e o Chatwoot continuava com as
        // permissões antigas — sem nada indicando a divergência. É diferente do `catch` do
        // `provisionUser`, que é não-fatal de propósito para não trancar o lojista fora do
        // Atendimento; aqui quem está na frente é o super admin, e ele tem de saber.
        let avisoChatwoot: string | undefined;

        if (store?.chatwootAccountId) {
            try {
                // Sincronizar todos os cargos que possuem sobrescrição para esta loja
                const token = await (ChatwootPlatformService as any).getMasterAdminToken(store.id, store.chatwootAccountId);

                if (permissions && typeof permissions === 'object' && !Array.isArray(permissions)) {
                    const roleNames = Object.keys(permissions);
                    for (const roleName of roleNames) {
                        await (ChatwootPlatformService as any).ensureCustomRole(store.id, store.chatwootAccountId, token, roleName);
                    }
                } else if (Array.isArray(permissions)) {
                    // Legado: OWNER
                    await (ChatwootPlatformService as any).ensureCustomRole(store.id, store.chatwootAccountId, token, "OWNER");
                }
            } catch (cwE: any) {
                console.error("[CHATWOOT] Failed to immediate sync store permissions:", cwE);
                avisoChatwoot =
                    "As permissões foram salvas na Mulply, mas o Chatwoot recusou a sincronização: " +
                    `${cwE?.message ?? "erro desconhecido"}. Os cargos lá continuam como estavam.`;
            }
        }

        await prisma.auditLog.create({
            data: {
                action: "STORE_CHATWOOT_PERMISSIONS_UPDATE",
                resource: "Store",
                resourceId: storeId,
                details: { permissions, chatwootSyncFalhou: Boolean(avisoChatwoot) },
                storeId
            }
        });

        // Sem as barras avulsas o caminho não existia e a rota NUNCA revalidava — a tela seguia
        // exibindo o valor anterior depois de salvar.
        revalidatePath(`/admin/super/stores/[slug]`, 'page');

        if (avisoChatwoot) return { success: false, error: avisoChatwoot };
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

// --- Store Notification Action ---

export async function sendStoreNotificationAction(
    storeId: string,
    notification: { subject: string; message: string; priority: "low" | "medium" | "high" }
): Promise<ActionResponse> {
    try {
        const adminSession = await getAdminSession('platform.stores.manage');

        const store = await prisma.store.findUnique({
            where: { id: storeId },
            select: { name: true }
        });

        if (!store) {
            return { success: false, error: "Loja não encontrada" };
        }

        // Get all users from this store
        const userStores = await prisma.userStore.findMany({
            where: { storeId },
            include: {
                user: {
                    select: { id: true, email: true, name: true }
                }
            }
        });

        // Map priority string to enum
        const priorityMap = {
            low: "LOW" as const,
            medium: "MEDIUM" as const,
            high: "HIGH" as const
        };

        // Create in-app notifications for all users
        const notificationPromises = userStores.map(userStore =>
            prisma.notification.create({
                data: {
                    userId: userStore.user.id,
                    storeId: storeId,
                    subject: notification.subject,
                    message: notification.message,
                    priority: priorityMap[notification.priority],
                    isRead: false
                }
            })
        );

        await Promise.all(notificationPromises);

        // Create audit log entry for the notification
        await prisma.auditLog.create({
            data: {
                action: "STORE_NOTIFICATION_SENT",
                resource: "Store",
                resourceId: storeId,
                details: {
                    subject: notification.subject,
                    message: notification.message,
                    priority: notification.priority,
                    recipients: userStores.length,
                    recipientEmails: userStores.map(us => us.user.email),
                    sentBy: adminSession.user.email,
                    storeName: store.name
                },
                storeId,
                userId: adminSession.user.id,
                userEmail: adminSession.user.email
            }
        });

        revalidatePath("/admin/super/stores");
        return {
            success: true,
            data: {
                recipients: userStores.length,
                storeName: store.name
            }
        };
    } catch (error: any) {
        console.error("Erro ao enviar notificação:", error);
        return { success: false, error: error.message };
    }
}

// --- Release Actions ---

export async function listReleasesAction(includeUnpublished: boolean = false): Promise<ActionResponse> {
    try {
        const where: any = {};

        if (!includeUnpublished) {
            where.isPublished = true;
        }

        const releases = await prisma.release.findMany({
            where,
            orderBy: { publishedAt: 'desc' },
            include: {
                author: {
                    select: {
                        name: true,
                        email: true
                    }
                }
            }
        });

        return { success: true, data: releases };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function getReleaseBySlugAction(slug: string): Promise<ActionResponse> {
    try {
        const release = await prisma.release.findUnique({
            where: { slug },
            include: {
                author: {
                    select: {
                        name: true,
                        email: true
                    }
                }
            }
        });

        if (!release) {
            return { success: false, error: "Release não encontrado" };
        }

        return { success: true, data: release };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function createReleaseAction(data: {
    title: string;
    slug: string;
    content: string;
    excerpt?: string;
    category: string;
    tags: string[];
    isPublished: boolean;
}): Promise<ActionResponse> {
    try {
        const adminSession = await getAdminSession();

        // Check if slug already exists
        const existing = await prisma.release.findUnique({
            where: { slug: data.slug }
        });

        if (existing) {
            return { success: false, error: "Slug já existe" };
        }

        const release = await prisma.release.create({
            data: {
                title: data.title,
                slug: data.slug,
                content: data.content,
                excerpt: data.excerpt,
                category: data.category as any,
                tags: data.tags,
                isPublished: data.isPublished,
                publishedAt: data.isPublished ? new Date() : null,
                authorId: adminSession.user.id
            }
        });

        revalidatePath("/admin/super/releases");
        return { success: true, data: release };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function updateReleaseAction(id: string, data: {
    title?: string;
    slug?: string;
    content?: string;
    excerpt?: string;
    category?: string;
    tags?: string[];
    isPublished?: boolean;
}): Promise<ActionResponse> {
    try {
        await getAdminSession();

        const existing = await prisma.release.findUnique({
            where: { id }
        });

        if (!existing) {
            return { success: false, error: "Release não encontrado" };
        }

        // If publishing for first time, set publishedAt
        const updateData: any = { ...data };
        if (data.isPublished && !existing.isPublished) {
            updateData.publishedAt = new Date();
        }

        const release = await prisma.release.update({
            where: { id },
            data: updateData
        });

        revalidatePath("/admin/super/releases");
        return { success: true, data: release };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function deleteReleaseAction(id: string): Promise<ActionResponse> {
    try {
        await getAdminSession();

        await prisma.release.delete({
            where: { id }
        });

        revalidatePath("/admin/super/releases");
        return { success: true };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

// --- Webhooks Monitor Action ---

export async function getRecentWebhooksAction(): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.observability.view');

        // Get last 50 webhook events from AuditLog
        const webhooks = await prisma.auditLog.findMany({
            where: {
                action: {
                    startsWith: 'WEBHOOK_'
                }
            },
            orderBy: {
                createdAt: 'desc'
            },
            take: 50,
            include: {
                store: {
                    select: {
                        name: true
                    }
                }
            }
        });

        const isWebhookSuccess = (action: string) =>
            action.includes('SUCCESS') || action.includes('RECEIVED') || action.includes('SYNC');

        const isWebhookFail = (action: string) =>
            action.includes('FAIL') || action.includes('ERROR') || action.includes('UNAUTHORIZED');

        // Calculate stats
        const total = webhooks.length;
        const success = webhooks.filter(w => isWebhookSuccess(w.action)).length;
        const failed = webhooks.filter(w => isWebhookFail(w.action)).length;
        const successRate = total > 0 ? Math.round((success / total) * 100) : 0;

        // Transform data for frontend
        const webhooksData = webhooks.map(w => ({
            id: w.id,
            action: w.action,
            storeId: w.storeId || 'N/A',
            storeName: w.store?.name || 'Sistema',
            createdAt: w.createdAt,
            status: isWebhookSuccess(w.action) ? 'SUCCESS' : isWebhookFail(w.action) ? 'FAIL' : 'PENDING',
            details: w.details
        }));

        return {
            success: true,
            data: {
                webhooks: webhooksData,
                stats: {
                    total,
                    success,
                    failed,
                    successRate
                }
            }
        };
    } catch (error: any) {
        console.error(error);
        return { success: false, error: error.message };
    }
}

export async function updateStoreIntegrationLimitAction(storeId: string, newLimit: number | null): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.manage');

        // 1. Get current limit for audit traceability
        const override = await prisma.storeLimitOverride.findUnique({
            where: { storeId },
            select: { maxEcommerceIntegrations: true }
        });

        // 2. Perform Upsert
        await prisma.storeLimitOverride.upsert({
            where: { storeId },
            update: { maxEcommerceIntegrations: newLimit },
            create: {
                storeId,
                maxEcommerceIntegrations: newLimit
            }
        });

        // 3. Audit Logging
        const { AuditService } = await import("@/services/audit.service");
        await AuditService.log({
            userId: session.user.id,
            userEmail: session.user.email ?? undefined,
            action: "UPDATE_LIMITS",
            resource: "STORE",
            resourceId: storeId,
            details: {
                field: "maxEcommerceIntegrations",
                oldLimit: override?.maxEcommerceIntegrations ?? null,
                newLimit: newLimit,
                notes: "Manual override by Super Admin"
            },
            storeId: storeId
        });

        revalidatePath("/admin/super/stores");
        revalidatePath(`/admin/super/stores/${storeId}`);

        return { success: true };
    } catch (error: any) {
        console.error("Error updating integration limit:", error);
        return { success: false, error: error.message };
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// Discount Engine Actions
// ──────────────────────────────────────────────────────────────────────────────

const discountSchema = z.object({
    discountType: z.enum(["PERCENTAGE", "FIXED"] as const, {
        error: "Tipo de desconto é obrigatório"
    }),
    discountValue: z.number().min(0, "Valor deve ser maior ou igual a zero"),
    discountDuration: z.enum(["FOREVER", "ONCE", "REPEATING"] as const, {
        error: "Duração do desconto é obrigatória"
    }),
    discountDurationInMonths: z.number().min(1).optional()
});

export type ApplyDiscountDTO = z.infer<typeof discountSchema>;

export async function applyDiscountToStoreAction(
    storeId: string,
    data: ApplyDiscountDTO
): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.manage');

        // 1. Validate input
        const validated = discountSchema.parse(data);

        // 2. Additional validation: if REPEATING, months must be provided
        if (validated.discountDuration === "REPEATING" && !validated.discountDurationInMonths) {
            return {
                success: false,
                error: "Para desconto recorrente, o número de meses é obrigatório."
            };
        }

        // 3. Validate percentage value
        if (validated.discountType === "PERCENTAGE" && validated.discountValue > 100) {
            return {
                success: false,
                error: "Desconto percentual não pode ser maior que 100%."
            };
        }

        // 4. Find subscription
        const subscription = await prisma.subscription.findUnique({
            where: { storeId },
            select: {
                id: true,
                discountType: true,
                discountValue: true,
                discountDuration: true,
                discountDurationInMonths: true
            }
        });

        if (!subscription) {
            return {
                success: false,
                error: "Assinatura não encontrada para esta loja."
            };
        }

        // 5. Update subscription with discount
        await prisma.subscription.update({
            where: { storeId },
            data: {
                discountType: validated.discountType,
                discountValue: validated.discountValue,
                discountDuration: validated.discountDuration,
                discountDurationInMonths: validated.discountDurationInMonths || null
            }
        });

        // 6. Audit Log
        const { AuditService } = await import("@/services/audit.service");
        await AuditService.log({
            userId: session.user.id,
            userEmail: session.user.email ?? undefined,
            action: "APPLY_DISCOUNT",
            resource: "SUBSCRIPTION",
            resourceId: subscription.id,
            details: {
                storeId,
                previousDiscount: {
                    type: subscription.discountType,
                    value: subscription.discountValue,
                    duration: subscription.discountDuration,
                    months: subscription.discountDurationInMonths
                },
                newDiscount: {
                    type: validated.discountType,
                    value: validated.discountValue,
                    duration: validated.discountDuration,
                    months: validated.discountDurationInMonths
                },
                appliedBy: session.user.email
            },
            storeId: storeId
        });

        revalidatePath("/admin/super/stores");
        revalidatePath(`/admin/super/stores/${storeId}`);

        return { success: true, data: { message: "Desconto aplicado com sucesso!" } };
    } catch (error: any) {
        console.error("Error applying discount:", error);
        return { success: false, error: error.message };
    }
}

export async function saveFilterAction(name: string, params: string): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.view');
        const userId = session.user.id;

        // Check for existing filter with same name for this user (Overwrite UX)
        const existing = await (prisma as any).adminSavedFilter.findUnique({
            where: {
                userId_name: {
                    userId,
                    name
                }
            }
        });

        if (existing) {
            const updated = await (prisma as any).adminSavedFilter.update({
                where: { id: existing.id },
                data: { params }
            });
            return { success: true, data: { ...updated, updated: true } };
        }

        const saved = await (prisma as any).adminSavedFilter.create({
            data: {
                name,
                params,
                userId
            }
        });

        return { success: true, data: saved };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function deleteFilterAction(id: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.view');
        await (prisma as any).adminSavedFilter.delete({ where: { id } });
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function getSavedFiltersAction(): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.view');
        const filters = await (prisma as any).adminSavedFilter.findMany({
            where: { userId: session.user.id },
            orderBy: { updatedAt: 'desc' }
        });
        return { success: true, data: filters };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

/**
 * Upsert Store Note (Create or Update)
 * Allows admins to add/edit internal notes for a store
 */
const AddStoreNoteSchema = z.object({
    storeId: z.string().min(1, "O ID da loja é obrigatório"),
    title: z.string()
        .min(1, "O título da nota não pode estar vazio")
        .max(150, "O título não pode ter mais de 150 caracteres"),
    content: z.string()
        .min(1, "O conteúdo da nota não pode estar vazio")
        .max(5000, "O conteúdo não pode exceder 5000 caracteres")
});

export async function addStoreNoteAction(storeId: string, title: string, content: string): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.manage');

        const validatedFields = AddStoreNoteSchema.safeParse({ storeId, title, content });

        if (!validatedFields.success) {
            return { 
                success: false, 
                error: validatedFields.error.issues.map(e => e.message).join(", ") 
            };
        }

        const data = validatedFields.data;

        // Create new note
        const note = await prisma.storeNote.create({
            data: {
                storeId: data.storeId,
                adminId: session.user.id,
                title: data.title.trim(),
                content: data.content.trim()
            }
        });

        revalidatePath("/admin/super/stores");
        return { success: true, data: note };
    } catch (error: any) {
        console.error("Error adding store note:", error);
        return { success: false, error: error.message || "Erro ao salvar nota" };
    }
}

/**
 * Get Store Note for current admin
 */
export async function getStoreNotesAction(storeId: string): Promise<ActionResponse> {
    try {
        const session = await getAdminSession('platform.stores.view');

        const notes = await prisma.storeNote.findMany({
            where: {
                storeId
            },
            include: {
                admin: {
                    select: {
                        name: true
                    }
                }
            },
            orderBy: {
                createdAt: 'desc'
            }
        });

        return { success: true, data: notes };
    } catch (error: any) {
        console.error("Error fetching store notes:", error);
        return { success: false, error: error.message || "Erro ao buscar notas" };
    }
}

/**
 * Get all available store tags
 */
export async function getAllStoreTagsAction(): Promise<ActionResponse<PrismaClient.StoreTag[]>> {
    try {
        await getAdminSession('platform.stores.view');
        const tags = await prisma.storeTag.findMany({
            orderBy: { label: 'asc' }
        });
        return { success: true, data: tags };
    } catch (error: any) {
        console.error("Error fetching store tags:", error);
        return { success: false, error: error.message || "Erro ao buscar tags" };
    }
}

/**
 * Toggle a tag for a store
 */
export async function toggleStoreTagAction(storeId: string, tagId: string): Promise<ActionResponse> {
    try {
        await getAdminSession('platform.stores.manage');

        const existing = await prisma.storeToTag.findUnique({
            where: {
                storeId_tagId: {
                    storeId,
                    tagId
                }
            }
        });

        if (existing) {
            await prisma.storeToTag.delete({
                where: {
                    storeId_tagId: {
                        storeId,
                        tagId
                    }
                }
            });
        } else {
            await prisma.storeToTag.create({
                data: {
                    storeId,
                    tagId
                }
            });
        }

        revalidatePath("/admin/super/stores");
        return { success: true };
    } catch (error: any) {
        console.error("Error toggling store tag:", error);
        return { success: false, error: error.message || "Erro ao atualizar tag" };
    }
}

// ==========================================
// Gestão de Nós de Infraestrutura (BYOI)
// ==========================================

export async function searchStoresForInfraNode(query: string = ""): Promise<ActionResponse<any[]>> {
    try {
        await getAdminSession('platform.observability.view');
        const stores = await prisma.store.findMany({
            where: {
                name: { contains: query, mode: "insensitive" },
                isPlatform: false
            },
            take: 20,
            select: { id: true, name: true, slug: true }
        });
        return { success: true, data: stores };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function upsertInfraNode(data: {
    id?: string;
    name: string;
    type: 'CHATWOOT' | 'EVOLUTION';
    endpointUrl: string;
    apiKey: string;
    ssoSecret?: string;
    chatwootAccountId?: string | number;
    chatwootAuthMode?: 'PLATFORM' | 'CORE';
    isMulplySupport?: boolean;
    chatwootInboxId?: number;
    supportApiToken?: string;
    /**
     * Nó do pool compartilhado. Antes era `true` fixo na criação e não existia no update —
     * um nó só saía do pool virando exclusivo de uma loja (`linkNodeToStore`) ou entrando em
     * quarentena inativa (`unlinkNodeFromStore`). Foi assim que o nó de Suporte, criado só
     * para hospedar o chat da Mulply, entrou no sorteio de qual servidor atende cada lojista
     * e passou a empatar com o nó público de verdade.
     */
    isPublic?: boolean;
}): Promise<ActionResponse> {
    try {
        await getAdminSession();

        // Despublicar o ÚLTIMO nó público ativo de um tipo deixaria toda loja sem nó dedicado
        // sem servidor nenhum — `resolveNodeCredentials` lança "[FATAL] Sem Node disponível".
        if (data.id && data.isPublic === false) {
            const atual = await prisma.infraNode.findUnique({ where: { id: data.id } });
            if (atual?.isPublic && atual.isActive) {
                const outrosPublicos = await prisma.infraNode.count({
                    where: { type: atual.type, isPublic: true, isActive: true, id: { not: data.id } },
                });
                if (outrosPublicos === 0) {
                    return {
                        success: false,
                        error: `Este é o único nó ${atual.type} público ativo. Despublicá-lo deixaria sem servidor todas as lojas que não têm nó dedicado. Crie ou ative outro nó público antes.`,
                    };
                }
            }
        }

        const chatwootAuthMode = data.chatwootAuthMode || 'PLATFORM';
        const chatwootAccountId = data.chatwootAccountId ? parseInt(data.chatwootAccountId.toString(), 10) : null;

        // --- GATEKEEPER (Pre-flight Ping) ---
        // Apenas validamos se o token for fornecido/alterado (não é a máscara ••••••••)
        if (data.apiKey && data.apiKey !== "••••••••") {
            console.log(`[GATEKEEPER] Validando nó ${data.type} (${data.endpointUrl})...`);
            
            try {
                let isValid = false;
                let errorMsg = "Token inválido ou sem permissão.";

                if (data.type === 'EVOLUTION') {
                    const res = await fetch(`${data.endpointUrl}/instance/fetchInstances`, {
                        headers: { 'apikey': data.apiKey }
                    });
                    if (res.ok) isValid = true;
                    else errorMsg = `Evolution API recusou o token (${res.status})`;
                } else if (data.type === 'CHATWOOT') {
                    // authMode define o tipo de token → determina o endpoint de validação
                    // PLATFORM: Platform API Token → /platform/api/v1/accounts
                    // CORE (sem suporte): Account API Token → /api/v1/accounts/{id}/users
                    // CORE (com suporte): User Access Token → /api/v1/profile
                    const testUrl = chatwootAuthMode === 'PLATFORM'
                        ? `${data.endpointUrl}/platform/api/v1/accounts`
                        : data.isMulplySupport
                            ? `${data.endpointUrl}/api/v1/profile`
                            : `${data.endpointUrl}/api/v1/accounts/${chatwootAccountId}/users`;

                    const res = await fetch(testUrl, {
                        headers: { 'api_access_token': data.apiKey }
                    });
                    if (res.status === 401 || res.status === 403) {
                        errorMsg = `Chatwoot recusou o token (${res.status}). Verifique o link e o Modo (${chatwootAuthMode})`;
                    } else if (res.ok || res.status >= 500) {
                        isValid = true;
                    } else {
                        errorMsg = `Chatwoot retornou status inesperado (${res.status}). Verifique a URL e o Modo (${chatwootAuthMode})`;
                    }
                }

                if (!isValid) {
                    return { success: false, error: `[GATEKEEPER] Falha na validação do nó: ${errorMsg}` };
                }
                console.log(`[GATEKEEPER] Nó ${data.type} validado com sucesso.`);
            } catch (pingError: any) {
                console.error("[GATEKEEPER] Erro de rede ao validar nó:", pingError);
                return { success: false, error: `[GATEKEEPER] Não foi possível alcançar o nó no endereço informado: ${pingError.message}` };
            }
        }

        // VALIDAÇÃO ESTRITA (Fase 4.6.5): CORE mode exige Account ID
        if (data.type === 'CHATWOOT' && chatwootAuthMode === 'CORE') {
            if (!chatwootAccountId) {
                return { success: false, error: "O 'ID da Conta' é obrigatório quando o Modo de Autenticação é 'CORE'." };
            }
        }

        // Validação do token de suporte (se preenchido)
        if (data.supportApiToken && data.supportApiToken !== "••••••••" && data.isMulplySupport) {
            try {
                const testRes = await fetch(`${data.endpointUrl}/api/v1/profile`, {
                    headers: { 'api_access_token': data.supportApiToken },
                });
                if (!testRes.ok) {
                    return { success: false, error: `Token de suporte inválido — Chatwoot retornou ${testRes.status}. Verifique o Access Token do admin da conta de suporte.` };
                }
            } catch (err: any) {
                return { success: false, error: `Não foi possível validar o token de suporte: ${err.message}` };
            }
        }

        // Re-encriptação após o Ping (Data Safety)
        const apiKeyEncrypted = data.apiKey && data.apiKey !== "••••••••" ? encryptInfraToken(data.apiKey) : undefined;
        const ssoSecretEncrypted = data.ssoSecret && data.ssoSecret !== "••••••••" ? encryptInfraToken(data.ssoSecret) : undefined;
        const supportTokenEncrypted = data.supportApiToken && data.supportApiToken !== "••••••••" ? encryptInfraToken(data.supportApiToken) : undefined;

        // Unicidade: apenas 1 nó pode ser o nó de suporte Mulply
        if (data.isMulplySupport) {
            await prisma.infraNode.updateMany({
                where: { isMulplySupport: true, ...(data.id ? { id: { not: data.id } } : {}) },
                data: { isMulplySupport: false },
            });
        }

        let targetStoreId: string | undefined = undefined;

        if (data.id) {
            const existing = await prisma.infraNode.findUnique({ where: { id: data.id } });
            if (!existing) return { success: false, error: "Nó não encontrado." };

            // Se o nó for privado, capturamos a loja dona para invalidação cirúrgica
            if (existing.ownerStoreId) {
                targetStoreId = existing.ownerStoreId;
            }

            const updateData: any = {
                name: data.name,
                endpointUrl: data.endpointUrl,
                type: data.type,
                chatwootAuthMode: chatwootAuthMode,
                isMulplySupport: data.isMulplySupport ?? false,
            };
            // Só toca em `isPublic` se o formulário enviou o campo — chamador antigo que não
            // conhece a flag continua não mexendo no pool.
            if (data.isPublic !== undefined) updateData.isPublic = data.isPublic;
            if (apiKeyEncrypted) updateData.apiKey = apiKeyEncrypted;
            if (ssoSecretEncrypted) updateData.ssoSecret = ssoSecretEncrypted;
            if (supportTokenEncrypted) {
                const existingCreds = (existing.credentials as Record<string, unknown>) ?? {};
                updateData.credentials = { ...existingCreds, supportApiToken: supportTokenEncrypted };
            }
            // Só sobrescreve se o campo foi explicitamente preenchido no form
            if (chatwootAccountId !== null) updateData.chatwootAccountId = chatwootAccountId;
            if (data.chatwootInboxId !== undefined) updateData.chatwootInboxId = data.chatwootInboxId || null;

            await prisma.infraNode.update({ where: { id: data.id }, data: updateData });
        } else {
            if (!apiKeyEncrypted) {
                return { success: false, error: "A chave de API é obrigatória para criação de novos nós." };
            }
            await prisma.infraNode.create({
                data: {
                    name: data.name,
                    slug: `node-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                    type: data.type,
                    endpointUrl: data.endpointUrl,
                    apiKey: apiKeyEncrypted,
                    ssoSecret: ssoSecretEncrypted,
                    credentials: supportTokenEncrypted ? { supportApiToken: supportTokenEncrypted } : undefined,
                    chatwootAccountId: chatwootAccountId,
                    chatwootAuthMode: chatwootAuthMode,
                    isMulplySupport: data.isMulplySupport ?? false,
                    chatwootInboxId: data.chatwootInboxId ?? null,
                    // Padrão continua público (comportamento histórico), mas agora é escolha
                    // do formulário e não uma constante irreversível.
                    isPublic: data.isPublic ?? true,
                    isActive: true,
                }
            });
        }
        
        // Invalida o cache: Cirúrgico se for um nó privado, Global se for público
        ChatwootPlatformService.invalidateCache(targetStoreId);
        // Evolution mantém LRU próprio de credenciais (TTL 5 min) — sem este clear, a
        // chave VELHA do nó continua sendo enviada após a correção (incidente real em prod).
        const { EvolutionService } = await import("@/services/evolution.service");
        EvolutionService.clearAllNodeCache();
        // O Chatwoot também precisa: vincular/desvincular nó muda o roteamento dos DOIS
        // lados, e antes daqui só o Evolution era invalidado — o cache do Chatwoot seguia
        // servindo o nó antigo por até o TTL de 5 min, sem sinal nenhum.
        ChatwootPlatformService.invalidateCache();
        console.log(`[INFRA_UPDATE] Cache invalidation triggered. Target: ${targetStoreId || 'GLOBAL'}`);

        revalidatePath("/admin/super/infra/nodes");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function linkNodeToStore(nodeId: string, storeId: string): Promise<ActionResponse> {
    try {
        await getAdminSession();
        // Converte o nó para uso exclusivo / privado da loja
        await prisma.infraNode.update({
            where: { id: nodeId },
            data: {
                ownerStoreId: storeId,
                isPublic: false
            }
        });
        const { EvolutionService } = await import("@/services/evolution.service");
        EvolutionService.clearAllNodeCache();
        // O Chatwoot também precisa: vincular/desvincular nó muda o roteamento dos DOIS
        // lados, e antes daqui só o Evolution era invalidado — o cache do Chatwoot seguia
        // servindo o nó antigo por até o TTL de 5 min, sem sinal nenhum.
        ChatwootPlatformService.invalidateCache();
        revalidatePath("/admin/super/infra/nodes");
        revalidatePath("/admin/super/infra/health");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

export async function unlinkNodeFromStore(nodeId: string): Promise<ActionResponse> {
    try {
        await getAdminSession();
        // Remove a exclusividade e deixa em quarentena (Inativo e Privado/Falso)
        await prisma.infraNode.update({
            where: { id: nodeId },
            data: {
                ownerStoreId: null,
                isPublic: false,
                isActive: false
            }
        });
        const { EvolutionService } = await import("@/services/evolution.service");
        EvolutionService.clearAllNodeCache();
        // O Chatwoot também precisa: vincular/desvincular nó muda o roteamento dos DOIS
        // lados, e antes daqui só o Evolution era invalidado — o cache do Chatwoot seguia
        // servindo o nó antigo por até o TTL de 5 min, sem sinal nenhum.
        ChatwootPlatformService.invalidateCache();
        revalidatePath("/admin/super/infra/nodes");
        revalidatePath("/admin/super/infra/health");
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
}

/**
 * Shadow Sync: Sincroniza instâncias externas (Evolution/Chatwoot) com o banco local
 * Ferramenta exclusiva de Super Admin para desbloqueio BYOI
 */
export async function syncStoreExternalConnections(storeId: string): Promise<ActionResponse> {
    try {
        await getAdminSession();

        const store = await prisma.store.findUnique({
            where: { id: storeId }
        });

        if (!store) return { success: false, error: "Loja não encontrada." };

        // 0. Busca Independente de Nós (Híbrido)
        
        // Nó de Interface (Chatwoot)
        const chatwootNode = await prisma.infraNode.findFirst({
            where: {
                type: 'CHATWOOT',
                isActive: true,
                OR: [
                    { ownerStoreId: storeId },
                    { isPublic: true }
                ]
            },
            orderBy: { isPublic: 'asc' }
        });

        // Nó de Mensageria (Evolution)
        const evolutionNode = await prisma.infraNode.findFirst({
            where: {
                type: 'EVOLUTION',
                isActive: true,
                OR: [
                    { ownerStoreId: storeId },
                    { isPublic: true }
                ]
            },
            orderBy: { isPublic: 'asc' }
        });

        if (!chatwootNode) return { success: false, error: "Nenhum nó de Chatwoot configurado para esta loja." };
        if (!evolutionNode) return { success: false, error: "Nenhum nó de Evolution configurado para esta loja." };

        // Validação de Account ID Obrigatório no Nó de Chatwoot
        if (chatwootNode.chatwootAccountId === null || chatwootNode.chatwootAccountId === undefined) {
            return { success: false, error: "Account ID não informado no Nó Chatwoot. Edite o nó e preencha este campo antes de sincronizar." };
        }

        // Auto-Healing: Atualiza a Store se o ID estiver divergente ou nulo
        if (store.chatwootAccountId !== chatwootNode.chatwootAccountId) {
            console.log(`[SHADOW SYNC] Auto-healing: Sincronizando chatwootAccountId ${chatwootNode.chatwootAccountId} para loja ${store.name}`);
            await prisma.store.update({
                where: { id: storeId },
                data: { chatwootAccountId: chatwootNode.chatwootAccountId }
            });
        }

        const effectiveAccountId = chatwootNode.chatwootAccountId;

        console.log(`[SHADOW SYNC] Iniciando reconciliação híbrida para loja: ${store.name} (${storeId})`);
        console.log(`[SHADOW SYNC] Chatwoot Node: ${chatwootNode.name} (${chatwootNode.endpointUrl})`);
        console.log(`[SHADOW SYNC] Evolution Node: ${evolutionNode.name} (${evolutionNode.endpointUrl})`);

        // 1. Busca instâncias na Evolution
        const { EvolutionService } = await import("@/services/evolution.service");
        const evoInstances = await EvolutionService.fetchInstances(storeId);
        
        // 2. Busca inboxes no Chatwoot (com tratamento de 404)
        const { ChatwootPlatformService } = await import("@/services/chatwoot-platform.service");
        const masterToken = await ChatwootPlatformService.getMasterAdminToken(storeId, effectiveAccountId);
        
        let inboxesList: any[] = [];
        try {
            const chatwootInboxes = await ChatwootPlatformService.getInboxes(storeId, effectiveAccountId, masterToken);
            inboxesList = Array.isArray(chatwootInboxes) ? chatwootInboxes : (chatwootInboxes?.payload || []);
        } catch (error: any) {
            if (error.message?.includes("404")) {
                return { success: false, error: "Account ID não encontrado no servidor Chatwoot remoto. Verifique o número informado no nó." };
            }
            throw error;
        }

        console.log(`[SHADOW SYNC] Reconciliando ${evoInstances.length} instâncias com ${inboxesList.length} inboxes.`);

        // 3. Reconciliação (Upsert)
        for (const rawInstance of evoInstances) {
            const instance = rawInstance as any;
            const evoName = (instance?.instanceName || instance?.name || '').toLowerCase();
            const effectiveInstanceName = instance?.instanceName || instance?.name;

            if (!effectiveInstanceName) {
                console.warn(`[SHADOW SYNC] Ignorando instância sem nome detectada (Store: ${storeId})`);
                continue;
            }

            // Busca inbox correspondente pelo nome (Safe match)
            let matchedInbox = inboxesList.find((ib: any) => {
                const ibName = (ib?.name || '').toLowerCase();
                if (!ibName || !evoName) return false;
                return ibName.includes(evoName) || evoName.includes(ibName);
            });

            // Fallback se não der match pelo nome
            if (!matchedInbox) {
                matchedInbox = inboxesList.find((ib: any) => ib?.channel_type === 'Channel::Whatsapp');
                if (matchedInbox) {
                    console.warn(`[SHADOW SYNC] Match por nome falhou para a instância '${effectiveInstanceName}'. Usando fallback da primeira Inbox de WhatsApp (ID: ${matchedInbox?.id}).`);
                }
            }

            await prisma.whatsAppConnection.upsert({
                where: { instanceName: effectiveInstanceName },
                create: {
                    storeId,
                    instanceName: effectiveInstanceName,
                    displayName: effectiveInstanceName.split('-')[0] || "WhatsApp BYOI",
                    status: "CONNECTED",
                    // 🔴 P0-C. Esta linha é ADOTADA: a instância já existia no nó e não foi criada
                    // por nenhum ambiente nosso. O carimbo impede que o botão de excluir mande
                    // `logout`/`delete` nela — foi assim que 6 conexões de um cliente real ficaram
                    // alcançáveis pelo ambiente de teste.
                    //
                    // Por que não deixar NULL: NULL significa "legado" e cai no padrão do nome, e
                    // este mesmo bloco fabrica `displayName` A PARTIR do `instanceName` — logo uma
                    // instância adotada cujo nome já esteja no formato nativo passaria no padrão.
                    createdByAppUrl: MARCA_DE_ADOCAO,
                    chatwootInboxId: matchedInbox?.id || store.chatwootInboxId,
                    evolutionNodeId: evolutionNode.id // Correto: ID do nó de Evolution
                },
                update: {
                    status: "CONNECTED",
                    chatwootInboxId: matchedInbox?.id || store.chatwootInboxId,
                    evolutionNodeId: evolutionNode.id // Correto: ID do nó de Evolution
                }
            });

            // Atualização de Fallback na Store (Opcional, se a conta for nova)
            if (!store.whatsappInstanceName) {
                await prisma.store.update({
                    where: { id: storeId },
                    data: { 
                        whatsappInstanceName: effectiveInstanceName,
                        chatwootInboxId: matchedInbox?.id || store.chatwootInboxId
                    }
                });
            }
        }

        // ── Reconciliação multi-canal (Chatwoot-driven) ──────────────────────────
        // Delegada ao ChannelMirrorService: a MESMA reconciliação que o lojista dispara
        // por `syncMyChannelsAction`. Antes esta lógica vivia duplicada aqui, e os dois
        // caminhos divergiriam no primeiro bugfix (inclusive na conta do Chatwoot lida).
        // `mirrorInboxes` reusa as inboxes já buscadas acima — sem segundo round-trip e
        // sem o throttle, que não deve valer para uma ação deliberada de operação.
        const { ChannelMirrorService } = await import("@/services/channel-mirror.service");
        const espelho = await ChannelMirrorService.mirrorInboxes(storeId, inboxesList);

        console.log(
            `[SHADOW SYNC] E-mail: ${espelho.email.criadas} criada(s), ${espelho.email.atualizadas} atualizada(s) · ` +
            `Instagram: ${espelho.instagram.criadas} criada(s), ${espelho.instagram.atualizadas} atualizada(s) · ` +
            `Messenger: ${espelho.messenger.criadas} criada(s), ${espelho.messenger.atualizadas} atualizada(s) ` +
            `(loja ${storeId}).`
        );
        if (espelho.instagram.foraDoLimite > 0 || espelho.messenger.foraDoLimite > 0) {
            console.warn(
                `[SHADOW SYNC] Fora do limite do plano (espelhadas inativas): ` +
                `${espelho.instagram.foraDoLimite} Instagram, ${espelho.messenger.foraDoLimite} Messenger.`
            );
        }
        if (espelho.instagram.ausentes > 0 || espelho.messenger.ausentes > 0) {
            console.warn(
                `[SHADOW SYNC] Marcadas MISSING (inbox sumiu do Chatwoot): ` +
                `${espelho.instagram.ausentes} Instagram, ${espelho.messenger.ausentes} Messenger.`
            );
        }
        if (espelho.desconhecidas.count > 0) {
            console.warn(
                `[SHADOW SYNC] ${espelho.desconhecidas.count} inbox(es) com channel_type desconhecido ignorada(s): ` +
                `${espelho.desconhecidas.tipos.join(', ')}`
            );
        }
        // ─────────────────────────────────────────────────────────────────────────

        revalidatePath("/dashboard", "layout");
        revalidatePath("/admin/super/infra/nodes");

        return { success: true };
    } catch (error: any) {
        console.error(`[SHADOW SYNC ERROR] Store ${storeId}:`, error);
        return { success: false, error: error.message };
    }
}
